import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import QRCode from 'qrcode';
import { api, imageResearch } from './api.js';
import './styles.css';
import { weinKapitel, qualitaetsKarten, herstellungsSchritte, aromenKarten, inhaltsstoffKarten, fehlerKarten, kuecheKarten, geschichtsKarten } from './weinwissen.js';

const gradeOptions = ['Natives Olivenöl extra', 'Natives Olivenöl', 'Olivenöl (Mischung)'];
const categoryOptions = ['Vergleichsöl', 'Premiumöl'];
// Themen der Einstiegsseite; neue Einführungen (Aromen, Brot, Wein …) hier ergänzen
const themen = [
  {
    titel: 'Olivenöl',
    text: 'Eine kompakte Einführung für Köche: Güteklassen, Verarbeitung, Kosten, Inhaltsstoffe und kulinarische Verwendung.',
    pfad: '/gustumejo/olivenoel/'
  },
  {
    titel: 'Wein',
    text: 'Eine kompakte Einführung für Köche: Qualitätsstufen, Herstellung, Aromen, Inhaltsstoffe, Fehler und kulinarische Verwendung.',
    pfad: '/gustumejo/wein/',
    nurAdmin: true
  }
];
const gradeCards = [
  {
    title: 'Olivenöl',
    text: 'Mischung aus raffiniertem und nativem Olivenöl. Mild, stabil, sensorisch reduziert.',
    comparisonKey: 'Olivenöl (Mischung)',
    components: ['raffiniertes Olivenöl als Hauptbestandteil', 'kleiner Anteil natives Olivenöl oder natives Olivenöl extra zur Abrundung', 'freie Säure maximal 1,0 %', 'reines raffiniertes Olivenöl (max. 0,3 %) wird nicht an Endverbraucher verkauft', 'gesetzlich genießbar, aber sensorisch nicht auf Fehlerfreiheit im Sinne von nativ extra ausgerichtet'],
    premiumDifference: 'Gegenüber einem Premiumöl ist der Abstand sehr groß: Triglyceride und Ölsäure bleiben zwar ähnlich, aber Polyphenole, Oleocanthal/Oleacein, Hydroxytyrosol-/Tyrosolderivate, Pigmente und flüchtige Aromastoffe sind durch Raffination stark reduziert. Sensorisch bleibt ein neutrales, stabiles Fett statt eines ausdrucksstarken Würzöls.',
    kitchen: 'Pragmatisch für neutrale warme Anwendungen, Mayonnaisen, Marinaden oder Situationen, in denen Olivenölcharakter nicht im Vordergrund stehen soll.',
    sensory: ['mild', 'wenig bitter', 'wenig scharf', 'kaum Sortencharakter']
  },
  {
    title: 'Nativ',
    text: 'Mechanisch gewonnen, aber mit möglichen sensorischen Schwächen. Gut zum Lernen von Unterschieden.',
    comparisonKey: 'Nativ',
    components: ['ausschließlich mechanisch gewonnenes Olivenöl', 'keine Raffination', 'darf leichte sensorische Fehler zeigen', 'freie Säure höher zulässig als bei nativ extra'],
    premiumDifference: 'Gegenüber einem Premiumöl sind vor allem Polyphenole, frische Aromastoffe und Präzision geringer. Freie Fettsäuren dürfen bis 2,0 % liegen, sensorische Fehler sind möglich, Oxidationsprodukte können höher sein. Die Fettbasis bleibt ähnlich, aber die wertgebenden Begleitstoffe und die Frische liegen deutlich darunter.',
    kitchen: 'Ein Lernöl für die Küche: geeignet für einfache Anwendungen, aber weniger stark als bewusstes Finish oder sensorischer Akzent.',
    sensory: ['einfachere Frucht', 'mögliche dumpfe Noten', 'weniger klare Bitterkeit', 'kürzerer Abgang']
  },
  {
    title: 'Nativ extra',
    text: 'Mechanisch gewonnen, niedrige freie Säure, keine sensorischen Fehler.',
    comparisonKey: 'Nativ extra',
    components: ['ausschließlich mechanisch gewonnen', 'keine sensorischen Fehler', 'freie Säure maximal 0,8 %', 'definierte chemische Qualitätsparameter'],
    premiumDifference: 'Rechtlich ist ein Premiumöl ebenfalls nativ extra; der Unterschied liegt darin, wie weit ein Öl die Anforderungen übertrifft. Ein gewöhnliches natives Olivenöl extra darf bis 0,8 % freie Fettsäuren haben, enthält meist weniger Polyphenole (häufig etwa 100–500 mg/kg) und mittlere bis hohe Oleocanthal-/Oleacein-Werte, weniger frische flüchtige Aromastoffe und oft mehr Oxidationsprodukte. Es ist in der Regel weniger selektiv geerntet, weniger frisch und weniger intensiv – dafür aber deutlich günstiger.',
    kitchen: 'Vielseitig für kalt und warm. Gute native Olivenöle extra können Braten, Gemüse, Fisch, Pasta und Finish abdecken.',
    sensory: ['fruchtig', 'bitter möglich', 'scharf möglich', 'fehlerfrei', 'sorten- oder herkunftstypisch']
  },
  {
    title: 'Premiumöl',
    text: 'Premiumauswahl: frühe Ernte, geringe Ausbeute, schnelle Verarbeitung, hohe sensorische Präzision.',
    comparisonKey: 'Premiumöl',
    components: ['frühe, selektive Ernte', 'kurze Zeit zwischen Ernte und Mühle', 'kontrollierte Extraktion', 'oft sortenrein oder bewusst komponiert', 'bessere Lagerung und Verpackung', 'Orientierung: ab 250 mg/kg Health-Claim-Schwelle der EU; griechische Forschende empfehlen „hochphenolisch“ erst ab mehr als 500 mg/kg', 'Begriffe wie „SuperOlio“ sind Marketing- bzw. Buchbegriffe ohne offizielle Definition oder Grenzwert'],
    premiumDifference: 'Premiumöl ist keine eigene gesetzliche Güteklasse, sondern der Premiummaßstab innerhalb nativ extra: sehr niedrige freie Fettsäuren, sehr hohe Polyphenole, frische Aromastoffe, natürliche Pigmente, hoher Oleocanthal-/Oleacein-Anteil und extrem niedrige Oxidationswerte.',
    kitchen: 'Als Gewürz verstehen: wenige Gramm als Finish können Bitterkeit, Schärfe, Frische und Länge in ein Gericht bringen.',
    sensory: ['präzise Frucht', 'deutliche Bitterkeit', 'Rachenschärfe', 'langer Abgang', 'hohe Eigenständigkeit']
  }
];
const gradeComparison = [
  ['Triglyceride', '~98-99 %', '~98-99 %', '~98-99 %', '>99 %'],
  ['Ölsäure', '55-83 %', '55-83 %', '55-83 %', '55-83 %'],
  ['freie Fettsäuren', 'meist <0,2 %', '<=0,8 %', '<=2,0 %', '<=1,0 %'],
  ['Polyphenole gesamt', '>=250 mg/kg (EU-Health-Claim), Spitzenöle >1.000', '~100-500 mg/kg', 'häufig ~50-300 mg/kg', 'meist sehr wenig'],
  ['Oleocanthal/Oleacein', 'sehr hoch', 'mittel-hoch', 'eher geringer', 'Spuren/kaum'],
  ['Hydroxytyrosol- und Tyrosol\u00ADderivate', 'hoch', 'mittel-hoch', 'mittel', 'stark reduziert'],
  ['α-Tocopherol (Vit. E)', '~150-400 mg/kg', '~100-300 mg/kg', 'ähnlich/variabler', 'teilweise reduziert'],
  ['Squalen', '~2-7 g/kg', '~2-7 g/kg', 'ähnlich', 'größtenteils erhalten, teils vermindert'],
  ['Phytosterine', '~1-2 g/kg', '~1-2 g/kg', '~1-2 g/kg', 'relativ gut erhalten'],
  ['Chlorophyll/Pigmente', 'natürlich vorhanden', 'vorhanden', 'vorhanden', 'stark reduziert'],
  ['Carotinoide', 'vorhanden', 'vorhanden', 'vorhanden', 'stark reduziert'],
  ['flüchtige Aromastoffe', 'sehr viel/frisch', 'viel', 'weniger/ggf. Fehler', 'nahezu keine'],
  ['LOX-Derivate (C5/C6)', 'ca. 15–25 mg/kg (Schätzung)', 'ca. 8–15 mg/kg (Schätzung)', 'ca. 3–10 mg/kg (Schätzung)', '<1 mg/kg (Schätzung)'],
  ['Oxidationsprodukte', 'extrem niedrig', 'niedrig', 'höher zulässig', 'durch Raffination teils entfernt'],
  ['Geschmack', 'intensiv fruchtig, bitter, scharf', 'fruchtig, fehlerfrei', 'leichte sensorische Fehler möglich', 'neutral']
];
const gradeComparisonColumns = ['Premiumöl', 'Nativ extra', 'Nativ', 'Olivenöl (Mischung)'];
const costCards = [
  {
    title: 'Raffiniert / Olivenöl',
    subtitle: 'industriell, neutral, hohe Ausbeute',
    production: 'ca. 2-5 EUR/l',
    marketing: 'ca. 0,50-2 EUR/l',
    retail: 'ca. 4-12 EUR/l',
    note: 'Raffination glättet Fehler und stabilisiert, entfernt aber Aromastoffe, Pigmente und viele wertgebende Begleitstoffe.'
  },
  {
    title: 'Nativ',
    subtitle: 'mechanisch gewonnen, einfache Qualität',
    production: 'ca. 4-8 EUR/l',
    marketing: 'ca. 1-3 EUR/l',
    retail: 'ca. 7-18 EUR/l',
    note: 'Kosten steigen durch mechanische Gewinnung und Herkunft, aber Rohware, Frische und Sensorik sind nicht konsequent premiumorientiert.'
  },
  {
    title: 'Nativ extra',
    subtitle: 'fehlerfrei, gesetzliche Spitzenklasse',
    production: 'ca. 6-14 EUR/l',
    marketing: 'ca. 2-6 EUR/l',
    retail: 'ca. 12-30 EUR/l',
    note: 'Sorgfältigere Ernte, schnelle Verarbeitung, Filtration, Analyse und bessere Verpackung erhöhen die realen Literkosten.'
  },
  {
    title: 'Premiumöl',
    subtitle: 'frühe Ernte, kleine Chargen, maximale Kontrolle',
    production: 'ca. 12-35+ EUR/l',
    marketing: 'ca. 5-20 EUR/l',
    retail: 'ca. 30-90+ EUR/l',
    note: 'Niedrige Ausbeute, Handarbeit, Selektion, Wettbewerbe, Direktimport, kleine Gebinde und gastronomische Positionierung treiben den Preis.'
  }
];
const marketSources = [
  'IOC, Olive sector statistics December 2025 and forecasts. <https://www.internationaloliveoil.org/olive-sector-statistics-december-2025-and-forecasts/>',
  'MAPA-Konsumpanel 2024, zitiert im Kampagnenbericht 2024/25 der Junta de Andalucía. <https://ws142.juntadeandalucia.es/agriculturaypesca/observatorio/servlet/FrontController?action=DownloadS&table=12030&element=5153765&field=DOCUMENTO>'
];
const ingredientCards = [
  {
    title: 'Ölsäure',
    text: 'Prägende einfach ungesättigte Fettsäure, wichtig für Stabilität und ernährungsphysiologischen Wert.',
    rowLabel: 'Ölsäure',
    whyItMatters: 'Ölsäure ist die wichtigste Fettsäure im Olivenöl. Sie prägt die ernährungsphysiologische Qualität und trägt zur thermischen und oxidativen Stabilität bei.',
    premiumSignal: 'Beim Premiumöl ist der Ölsäurebereich nicht automatisch höher als bei normalem nativ extra. Der Premiumabstand entsteht stärker durch Frische, Polyphenole, Aromastoffe und niedrige Oxidation.',
    kitchen: 'Hohe Ölsäure unterstützt den Einsatz in der warmen Küche. Für den Geschmack ist sie aber weniger entscheidend als die Begleitstoffe.'
  },
  {
    title: 'Polyphenole',
    text: 'Tragen zu Bitterkeit, Schärfe und Oxidationsschutz bei. Früh geerntete Öle zeigen oft mehr davon.',
    rowLabel: 'Polyphenole gesamt',
    whyItMatters: 'Polyphenole sind zentrale Begleitstoffe hochwertiger nativer Olivenöle. Sie schützen vor Oxidation und sind sensorisch oft als Bitterkeit, Pikanz und Rachenkratzen wahrnehmbar.',
    premiumSignal: 'Hier liegt einer der größten realen Premiumabstände. Eine gesetzliche Premiumgrenze gibt es nicht; als offizieller Bezugspunkt dient die Health-Claim-Schwelle der VO (EU) 432/2012: mindestens 5 mg Hydroxytyrosol und Derivate je 20 g Öl, also 250 mg/kg. Premiumöle liegen darüber; Spitzenöle können über 1.000 mg/kg erreichen, während einfache native oder raffinierte Öle deutlich darunter liegen.',
    evidence: 'In Laborstudien erfüllten je nach Region etwa 40–88 % der untersuchten Proben die 250-mg/kg-Schwelle: Epirus 2022/23 ca. 40 %, griechische Labordatenbank mit 5.764 Proben aus 11 Jahren 74 %, Toskana und Apulien 2017/18 ca. 88 %. Das sind keine Marktanteile, denn die Proben sind ausgewählt. Außerdem sind die Messmethoden (NMR, HPLC nach IOC, HPLC nach Hydrolyse, Folin) nicht direkt vergleichbar.',
    recommendation: 'Die Autoren der griechischen Langzeitstudie empfehlen, erst ab mehr als 500 mg/kg von „hochphenolisch“ zu sprechen, damit der Health-Claim-Wert auch nach etwa 12 Monaten Lagerung noch erreicht wird. Ab mehr als 1.200 mg/kg sprechen sie von außergewöhnlich hohem Phenolgehalt.',
    sources: [
      'Epirus 2022/23, 198 Proben, NMR. <https://pmc.ncbi.nlm.nih.gov/articles/PMC10934859/>',
      'Griechische Langzeitstudie, 5.764 Proben, qNMR (Molecules 2021). <https://www.mdpi.com/1420-3049/26/4/1115>',
      'Toskana und Apulien 2017/18, 108 Öle, HPLC nach Hydrolyse. <https://pmc.ncbi.nlm.nih.gov/articles/PMC6600398/>'
    ],
    kitchen: 'Polyphenolreiche Öle wirken wie ein Würzmittel: ideal als Finish zu Gemüse, Hülsenfrüchten, Fleisch, Brot und bitteren Salaten.'
  },
  {
    title: 'Tocopherole',
    text: 'Vitamin-E-Verbindungen, relevant für Schutz vor Oxidation und Qualitätserhalt.',
    rowLabel: 'α-Tocopherol (Vit. E)',
    whyItMatters: 'α-Tocopherol ist die wichtigste Vitamin-E-Form im Olivenöl. Es wirkt antioxidativ und unterstützt die Haltbarkeit, ersetzt aber keine gute Lagerung.',
    premiumSignal: 'Premiumöle liegen häufig im Bereich von etwa 150-400 mg/kg. Nativ extra liegt oft bei 100-300 mg/kg; raffinierte Öle können teilweise reduziert sein.',
    kitchen: 'Tocopherole helfen bei Stabilität, sind aber kein Freibrief für schlechte Lagerung, Licht oder stark überhitzte Anwendung.'
  },
  {
    title: 'Freie Fettsäuren',
    text: 'Ein Qualitätsmarker für Fruchtzustand und Verarbeitung. Niedrige Werte sprechen für gesunde Oliven und schnelle, saubere Gewinnung.',
    rowLabel: 'freie Fettsäuren',
    whyItMatters: 'Freie Fettsäuren entstehen, wenn Triglyceride gespalten werden. Das passiert stärker bei beschädigten, überreifen, schlecht gelagerten oder mikrobiell belasteten Oliven.',
    premiumSignal: 'Premiumöle liegen meist unter 0,2 %. Nativ extra darf bis 0,8 %, natives Olivenöl bis 2,0 %. Raffinierte Öle können analytisch niedrig wirken, weil Raffination Säure entfernt, nicht weil die Rohware hochwertig war.',
    kitchen: 'Freie Fettsäuren sind kein Aromastoff, aber ein wichtiger Hinweis auf Sorgfalt. Sehr niedrige Werte stützen Frische, Stabilität und Premiumanspruch.'
  },
  {
    title: 'C5/C6-Verbindungen (LOX-Derivate)',
    text: 'Flüchtige Aromastoffe aus dem Lipoxygenase-Weg. Verantwortlich für grüne, grasige und fruchtige Noten wie frisch geschnittenes Gras, Tomatenblatt oder Artischocke.',
    rowLabel: 'LOX-Derivate (C5/C6)',
    whyItMatters: 'C5/C6-Verbindungen entstehen durch enzymatische Oxidation von Linol- und Linolensäure über den LOX-Weg. Sie prägen das typische Aroma von nativem Olivenöl extra: Hexanal (grün), trans-2-Hexenal (Tomatenblatt), 1-Hexanol (Gras) oder Hexylacetat (fruchtig).',
    premiumSignal: 'Grobe Schätzung, stark abhängig von Sorte und Messmethode: Premiumöle enthalten oft ca. 15–25 mg/kg LOX-Derivate, Nativ extra ca. 8–15 mg/kg, Nativ ca. 3–10 mg/kg. Raffinierte Öle enthalten kaum noch LOX-Derivate, weil die Desodorierung (Wasserdampfdestillation unter Vakuum) flüchtige Stoffe gezielt abtrennt; zusätzlich sind LOX-Derivate hitzeempfindlich.',
    kitchen: 'LOX-Derivate sind hitzeempfindlich und verflüchtigen sich beim Erhitzen. Ideal für kalte Anwendungen (Finish, Salate) oder kurzes Sautieren. Längeres Braten zerstört diese Aromen.'
  }
];
const aromaCards = [
  {
    title: 'Grün-fruchtige Aromen',
    text: 'Gras, grüne Tomate, Artischocke, Apfel und Kräuter entstehen vor allem aus flüchtigen C5-/C6-Verbindungen.',
    substances: ['Hexanal: grün, grasig, frisch', 'trans-2-Hexenal: grünes Blatt, Tomatenblatt, Mandelgrün', '1-Hexanol und cis-3-Hexenol: frisch geschnittenes Gras', 'Ester wie Hexylacetat: fruchtige Apfel- und Bananenanklänge'],
    created: 'Diese Stoffe entstehen überwiegend enzymatisch über den Lipoxygenase-Weg, sobald die Olive beim Mahlen aufgeschlossen wird. Entscheidend sind gesunde Früchte, Sorte, Reifegrad, Mahlung und eine kontrollierte kurze Malaxation.',
    lost: 'Sie gehen durch Sauerstoff, Wärme, zu lange Malaxation, lange Lagerung, Licht und Raffination verloren. Auch überreife oder beschädigte Oliven liefern weniger klare grüne Aromen.',
    practice: 'Beim Riechen zuerst an frisches Grün denken: Gras, Blatt, Tomatenstiel, Artischocke. Diese Noten sind ein gutes Zeichen für frische, schonend verarbeitete Ware.'
  },
  {
    title: 'Reife und nussige Noten',
    text: 'Reife Olive, Mandel, Nuss, Apfel und Banane wirken runder, können aber bei Alterung in dumpfe Noten kippen.',
    substances: ['Aldehyde und Alkohole aus reiferen Früchten', 'Ester für süßlich-fruchtige Eindrücke', 'milde oxidative Sekundärnoten bei beginnender Alterung', 'sortentypische Begleitstoffe aus Fruchtfleisch und Schale'],
    created: 'Sie entstehen stärker bei reiferem Erntezeitpunkt und je nach Sorte. Eine saubere Verarbeitung kann runde Frucht und Mandel erhalten, ohne dass das Öl müde wirkt.',
    lost: 'Reife Noten verschwimmen durch Oxidation, Wärme und alte Lagerung. Dann wird aus Mandel schnell walnussig-alt, aus reifer Frucht flach oder ranzig.',
    practice: 'Für Köche wichtig: reif-fruchtig ist nicht automatisch schlecht. Entscheidend ist, ob die Note sauber, klar und frisch bleibt.'
  },
  {
    title: 'Bitterkeit und Rachenschärfe',
    text: 'Nicht primär Geruch, aber sensorisch eng verbunden: Bitterkeit und Schärfe zeigen phenolische Begleitstoffe.',
    substances: ['Oleocanthal: pfeffrige Schärfe im Rachen', 'Oleacein: bitter, grün, antioxidativ relevant', 'Secoiridoide und Tyrosolderivate: Bitterkeit, Struktur, Persistenz', 'Tocopherole unterstützen Stabilität, sind aber sensorisch weniger direkt'],
    created: 'Hohe Werte entstehen durch frühe Ernte, gesunde Oliven, kurze Wege, kontrollierte Malaxation und geringe Wasserzugabe bei der Extraktion.',
    lost: 'Phenole sinken durch späte Ernte, starke Wasserzugabe, lange Prozesszeiten, Wärme, Sauerstoff und Lagerung. Raffination entfernt diese Stoffe weitgehend.',
    practice: 'Das Kratzen im Rachen ist bei guten Ölen kein Fehler. Es ist ein Lernsignal: Polyphenolreiches Öl wirkt am Teller wie Würze.'
  },
  {
    title: 'Fehlnoten als Aromasignale',
    text: 'Fehlnoten sind ebenfalls chemische Signale: Sie erzählen von beschädigter Frucht, Gärung, Oxidation oder falscher Lagerung.',
    substances: ['flüchtige Säuren und Ethylacetat: wein-/essigartig-säuerlich', 'Methyl- und Ethylester: fermentativ, gärig', 'Aldehyde wie Hexanal in oxidativem Kontext: ranzig, alt', 'Mikrobielle Stoffwechselprodukte: stichig/schlammiger Bodensatz, modrig-feucht-erdig'],
    created: 'Fehlnoten entstehen vor allem vor und nach der Mühle: verletzte Oliven, Säcke, Wärmestau, feuchte Lagerung, Bodensatz, Sauerstoff, Licht und Wärme.',
    lost: 'Raffination kann Fehlnoten technisch entfernen, nimmt aber gleichzeitig die frischen Aromen und viele wertgebende Begleitstoffe mit.',
    practice: 'In der Prüfung getrennt wahrnehmen: Ist es frisches Grün, reife Frucht oder ein Defekt? Besonders nach den Hauptfehlern stichig, modrig-feucht-erdig, wein-/essigartig und ranzig suchen.'
  }
];
const kitchenCards = [
  {
    title: 'Kalt',
    text: 'Salate, Brot, Gemüse, Burrata: Aroma bleibt vollständig sichtbar.',
    temperature: 'Raumtemperatur bis handwarm',
    description: 'Kalte Anwendungen zeigen die Qualität eines Olivenöls am ehrlichsten. Fruchtigkeit, Bitterkeit, Schärfe, Mandelnoten, Kräuter und Fehlnoten werden nicht von Röstaromen oder Hitze überdeckt.',
    uses: ['Premiumöl pur dosieren: als sensorischer Akzent auf Brot, Burrata, Tomaten, bitteren Salaten oder gegrilltem Gemüse.', 'Nativ extra als Basisöl: Vinaigrettes, Marinaden, Mayonnaisen, kalte Saucen und Gemüsegerichte.', 'Mildes nativ extra verwenden, wenn Fisch, Frischkäse oder feine Gemüse nicht überdeckt werden sollen.', 'Kräftiges, polyphenolreiches Öl einsetzen, wenn Bitterkeit und Schärfe bewusst Teil der Würzung sind.'],
    oilChanges: 'Bei kalter Verwendung bleibt das Öl weitgehend unverändert. Flüchtige Aromastoffe, Polyphenole und Schärfe sind maximal wahrnehmbar; Oxidation spielt nur über Zeit, Licht und Luftkontakt eine Rolle.',
    caution: 'Zu intensive Öle können feine Produkte dominieren. Bitterkeit und Schärfe gegen Süße, Fett und Säure balancieren.'
  },
  {
    title: 'Warm',
    text: 'Gemüse, Fisch, Pasta: extra nativ ist vielseitiger, als oft behauptet wird.',
    temperature: 'ca. 40-120 °C',
    description: 'Warme Küche löst Aromen und trägt sie in das Gericht, ohne das Öl zwingend stark zu erhitzen. Das ist oft der beste Kompromiss zwischen kulinarischem Effekt und Schonung hochwertiger Begleitstoffe.',
    uses: ['Nativ extra in Pasta, Risotto oder Hülsenfrüchten einarbeiten, um Bindung, Glanz und Frucht einzutragen.', 'Premiumöl erst am Ende in warme, nicht mehr kochende Gerichte geben: Suppen, Pürees, Bohnen, Fisch, Gemüse.', 'Mildere Öle für längeren Kontakt mit Wärme nutzen, intensive Öle kurz vor dem Servieren.', 'Raffiniertes Olivenöl nur dann wählen, wenn Aromaneutralität wichtiger ist als sensorische Tiefe.'],
    oilChanges: 'Mit Wärme verflüchtigen sich grüne Aromastoffe schneller, Polyphenole bleiben teilweise erhalten, wirken aber sensorisch weicher. Je länger die Warmhaltezeit, desto stärker sinken Frische und Präzision.',
    caution: 'Lange Warmhaltezeiten, Licht und offene Bain-Marie-Situationen lassen Frische und flüchtige Aromen schnell verschwinden.'
  },
  {
    title: 'Braten',
    text: 'Stabile native Olivenöle extra sind hitzetauglich; Premiumöle besser gezielt einsetzen.',
    temperature: 'Braten meist ca. 140-190 °C. Rauchpunkt grob: raffiniertes Olivenöl ca. 220-240 °C, gutes natives Olivenöl extra meist ca. 180-210 °C, abhängig von Qualität, freien Fettsäuren und Partikeln. Frittieren typischerweise ca. 160-180 °C.',
    description: 'Olivenöl kann zum Braten geeignet sein. Entscheidend sind Ölqualität, freie Fettsäuren, Filtration, Frische und Temperaturführung. Ein niedriger Säurewert und wenig Trub erhöhen die Hitzestabilität. Sehr hochwertige, polyphenolreiche Öle sind chemisch stabil, aber aromatisch zu wertvoll, um sie lange stark zu erhitzen.',
    uses: ['Filtriertes nativ extra für kontrolliertes Braten von Gemüse, Fisch, hellem Fleisch, Soffritto und mediterranen Saucenansätzen.', 'Raffiniertes Olivenöl für sehr neutrale Bratanwendungen oder wenn ein höherer Rauchpunkt wichtiger ist als Aroma.', 'Premiumöl nicht als langes Bratfett verschwenden: höchstens kurz mitwärmen oder nach dem Braten ergänzen.', 'Frittieren bei 160-180 °C mit stabilem, sauberem Öl; raffiniertes Olivenöl oder ein robustes, filtriertes nativ extra verwenden.'],
    oilChanges: 'Hitze beschleunigt Oxidation und baut flüchtige Aromen ab. Freie Fettsäuren, Trubpartikel und alte Ölqualität senken die Hitzestabilität. Polyphenole können schützen, gehen aber mit Zeit, Sauerstoff und wiederholtem Erhitzen zurück.',
    caution: 'Nicht bis zum sichtbaren Rauchen erhitzen. Rauch bedeutet Abbau, beißende Aromen und Qualitätsverlust. Trübe ungefilterte Öle, alte Öle und Öle mit vielen Partikeln rauchen früher.'
  },
  {
    title: 'Finish',
    text: 'Der gastronomisch stärkste Einsatz: wenige Gramm verändern ein Gericht deutlich.',
    temperature: 'nach dem Garen, direkt am Teller',
    description: 'Als Finish wird Olivenöl wie ein Gewürz eingesetzt. Es liefert Glanz, Duft, Frische, Bitterkeit, Schärfe und Länge. Genau hier rechtfertigt sich ein Premiumöl am stärksten, weil keine Hitze die flüchtigen Aromastoffe zerstört.',
    uses: ['Premiumöl als Würzöl direkt am Teller: Gemüse, Suppen, Pürees, Fisch, Fleisch, Carpaccio, Tatar.', 'Kräftige grüne Öle zu Bohnen, Linsen, gegrilltem Brot, bitteren Salaten, Rind oder gereiftem Käse.', 'Mildere Premiumöle zu Fisch, Frischkäse, feinen Gemüsen, Pasta und hellen Saucen.', 'Desserts gezielt abschließen: Schokolade, Vanilleeis, Zitrus, Steinobst oder Joghurt mit wenigen Tropfen Öl.'],
    oilChanges: 'Als Finish wird das Öl kaum thermisch belastet. Aromastoffe bleiben erhalten und steigen durch warme Speisen sogar stärker auf. Bitterkeit und Rachenschärfe bleiben klar wahrnehmbar.',
    caution: 'Dosierung ist entscheidend. Zu viel Premiumöl kann Speisen bitter, scharf oder schwer wirken lassen.'
  }
];
const processSteps = [
  {
    title: 'Ernte',
    short: 'Reifegrad und Methode bestimmen Ausbeute, Polyphenole und Verletzungsrisiko.',
    differences: ['Frühe Ernte: grünere Sensorik, mehr Bitterkeit und Schärfe, aber deutlich weniger Öl pro kg Oliven.', 'Späte Ernte: höhere Ausbeute, weichere Frucht, weniger phenolische Spannung.', 'Handernte oder schonende Rüttler vermeiden Quetschungen; aggressive Ernte und Bodenkontakt erhöhen Fehlerrisiken.'],
    cost: 'Frühe Ernte und sorgfältige Selektion können grob 2-6 EUR/l Mehrkosten verursachen; Handlese und geringe Baumleistung können bei Premiumölen deutlich darüber liegen.',
    faults: ['wein-/essigartig-säuerlich durch verletzte, gärende Früchte', 'erdig durch Fallobst mit Erd- oder Schlammanhaftung', 'modrig-feucht-erdig bei feucht gelagertem Lesegut (Schimmel)']
  },
  {
    title: 'Transport',
    short: 'Zeit, Temperatur und Belüftung entscheiden, ob die Olive frisch bleibt oder zu gären beginnt.',
    differences: ['Premiumbetriebe verarbeiten oft innerhalb weniger Stunden nach der Ernte.', 'Flache, luftige Kisten verhindern Druckstellen; Säcke und große Haufen stauen Wärme.', 'Kühlere Transporte bremsen Fermentation, sind aber logistisch anspruchsvoller.'],
    cost: 'Kurze Wege, kleine Kisten und schnelle Mühlentermine liegen oft bei etwa 0,50-2 EUR/l Mehrkosten; bei sehr kleinen Chargen mehr.',
    faults: ['stichig durch Gärung unter Luftabschluss in Säcken oder Haufen mit Wärmestau', 'wein-/essigartig-säuerlich durch Gärung mit Luftzutritt', 'modrig-feucht-erdig bei mehrtägiger feuchter Lagerung']
  },
  {
    title: 'Reinigung',
    short: 'Blätter, Erde, Staub und Fremdmaterial werden entfernt, bevor sie Aroma und Hygiene belasten.',
    differences: ['Saubere, gewaschene Früchte ergeben klarere Sensorik.', 'Zu viel Blattmaterial kann grüne, aber grobe Bitterkeit eintragen.', 'Schmutz und Erde erhöhen das Risiko dumpfer, unsauberer Noten.'],
    cost: 'Zusätzliche Reinigung, Wasseraufbereitung und langsamere Linienführung kosten meist etwa 0,10-0,50 EUR/l, verhindern aber teure Qualitätsverluste.',
    faults: ['erdig/schlammig bei unzureichender Reinigung', 'bitter-grün und adstringierend bei zu viel Blattanteil', 'metallisch bei ungepflegter Technik']
  },
  {
    title: 'Mahlen',
    short: 'Aus Oliven wird Paste; Sauerstoffeintrag, Mahlwerk und Partikelgröße prägen die spätere Extraktion.',
    differences: ['Hammermühlen arbeiten schnell und intensiv, können mehr Bitterkeit und Schärfe freisetzen. In einer Vergleichsstudie lieferte der Hammercrusher 563 statt 416 mg Gesamtphenole/kg (Kaffeesäure-Äquivalent) gegenüber der Steinmühle.', 'Steinmühlen sind traditionell, aber schwerer hygienisch und oxidativ zu kontrollieren.', 'Moderne Anlagen steuern Sauerstoff, Temperatur und Feinheit gezielt.'],
    cost: 'Moderne Mühlentechnik, Reinigung zwischen Chargen und Prozesskontrolle schlagen grob mit 0,30-1,50 EUR/l zu Buche.',
    faults: ['metallisch bei Abrieb oder schlechter Wartung', 'ranzig/oxidiert bei unnötigem Sauerstoffkontakt', 'dumpf bei unreiner Anlage']
  },
  {
    title: 'Malaxieren',
    short: 'Die Paste wird langsam gerührt; hier entstehen Extraktion, Aromaerhalt und viele Zielkonflikte.',
    differences: ['Kurz und kühl schützt Frische und Polyphenole, reduziert aber Ausbeute.', 'Länger und wärmer erhöht Ausbeute, kann flüchtige Aromen abbauen.', 'Geschlossene Systeme mit wenig Sauerstoff sind präziser, aber teurer.'],
    cost: 'Kalte, kurze Malaxation kann durch niedrigere Ausbeute etwa 1-4 EUR/l kosten; bei sehr früher Ernte und Premiumchargen mehr.',
    faults: ['erhitzt/verbrannt bei zu hoher Temperatur', 'ranzig/oxidiert bei Sauerstoffkontakt', 'flach und müde bei zu langer Prozesszeit']
  },
  {
    title: 'Zentrifugation',
    short: 'Öl, Wasser und Feststoffe werden getrennt; Wasserzugabe und Technik beeinflussen Aroma und Polyphenole.',
    differences: ['Zwei-Phasen-Anlagen sparen Wasser und können mehr Phenole erhalten.', 'Drei-Phasen-Anlagen arbeiten mit mehr Wasser und können wasserlösliche Begleitstoffe auswaschen.', 'Saubere, korrekt eingestellte Dekanter trennen schneller und schonender.'],
    cost: 'Moderne Dekanter, geringe Wasserzugabe und kleine Chargen verursachen etwa 0,30-1,50 EUR/l Mehrkosten gegenüber maximaler Ausbeute.',
    faults: ['vegetationswasserartig bei schlechter Trennung', 'schlammig bei mitgerissenen Feststoffen', 'flach bei Auswaschung aromatischer Begleitstoffe']
  },
  {
    title: 'Filtration',
    short: 'Trub und Restwasser werden entfernt; ungefiltert wirkt charmant, altert aber riskanter.',
    differences: ['Filtrierte Öle sind meist stabiler und klarer lagerfähig.', 'Ungefilterte Öle können früh fruchtig wirken, behalten aber Wasser und Partikel.', 'Sofortige Filtration verhindert gärige Trubnoten.'],
    cost: 'Filtration kostet inklusive Material, Zeit und Ölverlust meist 0,20-0,80 EUR/l, spart aber Reklamations- und Alterungsrisiko.',
    faults: ['stichig/schlammiger Bodensatz durch Trub, der im Tank gärt', 'modrig-feucht-erdig durch Restwasser und Mikroorganismen', 'ranzig bei beschleunigter Alterung']
  },
  {
    title: 'Lagerung',
    short: 'Licht, Sauerstoff, Wärme und Zeit entscheiden, ob Qualität erhalten bleibt.',
    differences: ['Edelstahltanks mit Inertgas schützen vor Sauerstoff.', 'Dunkle Flaschen oder Bag-in-Box reduzieren Lichtschäden.', 'Kühle, konstante Lagerung bewahrt Frische länger als warme Verkaufsräume.'],
    cost: 'Inertgas, Edelstahl, dunkle Gebinde und klimatisch bessere Lagerung liegen grob bei 0,50-2 EUR/l; bei kleinen Premiumgebinden mehr.',
    faults: ['ranzig durch Oxidation', 'strohig/alt durch Licht und Wärme', 'müde, flach, nussig-alt bei langer Lagerung']
  }
];

function App() {
  const [locationState, setLocationState] = useState({ path: window.location.pathname, search: window.location.search });
  const [user, setUser] = useState(undefined);
  const [myPanels, setMyPanels] = useState(null);
  const [error, setError] = useState('');
  const { path, search } = locationState;
  const panelParam = new URLSearchParams(search).get('panel');
  const panel = panelParam || (myPanels?.length === 1 ? myPanels[0].slug : null);
  const invitation = new URLSearchParams(search).get('invite');
  const resetToken = new URLSearchParams(search).get('reset');
  
  window.setLocationState = setLocationState;

  useEffect(() => {
    // Nur eine echte Ablehnung (4xx) bedeutet "nicht angemeldet"; Netzwerk- und Serverfehler nicht als Logout behandeln.
    api.me().then(setUser).catch((reason) => reason.status >= 400 && reason.status < 500 ? setUser(null) : setError('Der Server ist gerade nicht erreichbar.'));
    const onPopState = () => setLocationState({ path: window.location.pathname, search: window.location.search });
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!user) { setMyPanels([]); return; }
    setMyPanels(null);
    api.myPanels().then(setMyPanels).catch(() => setMyPanels([]));
  }, [user]);

  useEffect(() => {
    if (!user || user.role === 'ADMIN' || user.role === 'ORGANISATOR') return;
    if (panelParam || path !== '/gustumejo/' || !Array.isArray(myPanels) || myPanels.length !== 1) return;
    navigate(tastingUrl(myPanels[0].slug));
  }, [user, myPanels, panelParam, path]);

  if (path.startsWith('/gustumejo/olivenoel')) return <PresentationV2 />;
  if (error && user === undefined) return <Shell><Notice title="Verbindung unterbrochen" text={`${error} Bitte die Seite in einem Moment neu laden.`}><button onClick={() => window.location.reload()}>Neu laden</button></Notice></Shell>;
  if (user === undefined) return <Shell><Notice title="Wird geladen" text="Dein Zugang wird geprüft." /></Shell>;
  if (invitation) return <Activation token={invitation} onReady={setUser} />;
  if (resetToken) return <PasswordReset token={resetToken} onReady={setUser} />;
  if (!user) return <Login onReady={setUser} />;
  if (user.role === 'PARTICIPANT' && myPanels === null) return <Shell><Profile user={user} /><Notice title="Panels werden geladen" text="Deine zugeordneten Verkostungen werden geprüft." /></Shell>;
  if (path.startsWith('/gustumejo/wein') && user.role === 'ADMIN') return <WeinWissen />;
  if (path === '/') { navigate('/gustumejo/'); return <Shell><Notice title="Weiterleitung" text="Du wirst zur Panelauswahl weitergeleitet." /></Shell>; }
  if (path.startsWith('/gustumejo/admin/produkt/')) return user.role === 'ADMIN' ? <Shell><ProduktSeite user={user} produkt={decodeURIComponent(path.split('/gustumejo/admin/produkt/')[1].replace(/\/$/, ''))} /></Shell> : <PanelSelection user={user} panels={myPanels || []} />;
  if (path.startsWith('/gustumejo/admin')) return ['ADMIN', 'ORGANISATOR'].includes(user.role) ? <Admin user={user} /> : <PanelSelection user={user} panels={myPanels || []} />;
  if (!panel) return <PanelSelection user={user} panels={myPanels || []} />;
  return <AuthenticatedView user={user} panel={panel} path={path} />;
}

// Bei Blindproben (noch nicht aufgedeckt) tragen die Proben Buchstaben statt Nummern
function istVerdeckt(session) { return Boolean(session.blind && !session.aufgedeckt); }
function probeKennung(session, index) {
  if (!istVerdeckt(session)) return index + 1;
  return index < 26 ? String.fromCharCode(65 + index) : probeKennung(session, Math.floor(index / 26) - 1) + String.fromCharCode(65 + index % 26);
}

function tastingUrl(panel, suffix = '', oilId = '') {
  const path = suffix ? `/gustumejo/${suffix}` : '/gustumejo/';
  const oil = oilId ? `&oil=${encodeURIComponent(oilId)}` : '';
  return panel ? `${path}?panel=${encodeURIComponent(panel)}${oil}` : path;
}

function Login({ onReady }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [bootstrap, setBootstrap] = useState(false);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const targetPanel = new URLSearchParams(window.location.search).get('panel');

  useEffect(() => {
    api.bootstrapStatus().then(result => setBootstrap(result.needed));
  }, []);

  async function submit(event) {
    event.preventDefault();
    try {
      onReady(await api.login({ login: email, password }));
      navigate(targetPanel ? `/gustumejo/?panel=${encodeURIComponent(targetPanel)}` : '/gustumejo/');
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function firstAdmin(event) {
    event.preventDefault();
    try {
      onReady(await api.bootstrap(key, { name, email, password }));
      navigate('/gustumejo/admin/');
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function requestReset(event) {
    event.preventDefault();
    try {
      const result = await api.requestPasswordReset({ email: resetEmail });
      setMessage(result.mailSent ? 'Falls zu dieser Mailadresse ein aktiver Zugang existiert, wurde ein Link zum Zurücksetzen versendet.' : 'Der Passwort-Reset ist aktuell nicht verfügbar, weil der Mailversand noch nicht aktiviert ist.');
      setShowReset(false);
      setResetEmail('');
    } catch (error) {
      setMessage(error.message);
    }
  }

  return <Shell>
    {bootstrap
      ? <Hero eyebrow="Verkostung" title="Ersten Admin anlegen" text="Einmalige Initialisierung: Der ADMIN_KEY wird nur zum Anlegen des ersten Admin-Kontos benötigt." />
      : <Hero eyebrow="Gustumejo" title="Der Ort des Kostens" text="Hier verkosten Runden gemeinsam: im direkten Vergleich, mit festem Vokabular und ohne Punkte. Zum Einstieg gibt es Einführungen in die Themen." />}
    {!bootstrap && <Themen user={null} />}
    <section className={bootstrap ? 'panel authPanel' : 'panel authPanel authNachThemen'}>
      {!bootstrap && <><h2>Zur Verkostung</h2><p>Melde dich mit deinem Benutzernamen und Passwort an. Noch kein Konto? Öffne deinen persönlichen Einladungslink oder scanne den QR-Code.</p></>}
      <form onSubmit={bootstrap ? firstAdmin : submit}>
        {bootstrap && <input required value={name} onChange={e => setName(e.target.value)} placeholder="Name" />}
        {bootstrap
          ? <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Mailadresse" />
          : <input required autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck="false" value={email} onChange={e => setEmail(e.target.value)} placeholder="Benutzername oder Mailadresse" aria-label="Benutzername oder Mailadresse" />}
        <input required minLength="10" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" />
        {bootstrap && <input required type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="ADMIN_KEY" />}
        <button>{bootstrap ? 'Admin-Konto anlegen' : 'Anmelden'}</button>
      </form>
      {!bootstrap && <div className="authLinks"><button type="button" className="secondary textButton" onClick={() => { setShowReset(!showReset); setMessage(''); setResetEmail(email.includes('@') ? email : ''); }}>Passwort vergessen?</button></div>}
      {showReset && !bootstrap && <form className="authSubform" onSubmit={requestReset}>
        <input required type="email" value={resetEmail} onChange={event => setResetEmail(event.target.value)} placeholder="Mailadresse für den Reset" />
        <div className="actions compactActions">
          <button type="submit">Reset-Link anfordern</button>
          <button type="button" className="secondary" onClick={() => setShowReset(false)}>Abbrechen</button>
        </div>
      </form>}
      {message && <p className="message">{message}</p>}
    </section>
  </Shell>;
}
function Activation({ token,onReady }) { const [password,setPassword]=useState('');const [repeat,setRepeat]=useState('');const [message,setMessage]=useState(''); const targetPanel = new URLSearchParams(window.location.search).get('panel'); async function submit(e){e.preventDefault();if(password!==repeat)return setMessage('Die Passwörter stimmen nicht überein.');try{onReady(await api.activate(token,{password}));navigate(targetPanel ? `/gustumejo/?panel=${encodeURIComponent(targetPanel)}` : '/gustumejo/')}catch(error){setMessage(error.message)}}return <Shell><Hero eyebrow="Einladung" title="Konto aktivieren" text="Vergib ein Passwort, um deine Registrierung abzuschließen und deine Panels auf Smartphone oder Pad zu öffnen."/><section className="panel authPanel"><form onSubmit={submit}><input required minLength="10" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Passwort (mindestens 10 Zeichen)"/><input required type="password" value={repeat} onChange={e=>setRepeat(e.target.value)} placeholder="Passwort wiederholen"/><button>Konto aktivieren</button></form>{message&&<p className="message">{message}</p>}</section></Shell> }
function PasswordReset({ token, onReady }) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [message, setMessage] = useState('');
  const targetPanel = new URLSearchParams(window.location.search).get('panel');

  async function submit(event) {
    event.preventDefault();
    if (password !== repeat) return setMessage('Die Passwörter stimmen nicht überein.');
    try {
      onReady(await api.confirmPasswordReset(token, { password }));
      navigate(targetPanel ? `/gustumejo/?panel=${encodeURIComponent(targetPanel)}` : '/gustumejo/');
    } catch (error) {
      setMessage(error.message);
    }
  }

  return <Shell>
    <Hero eyebrow="Zugang" title="Neues Passwort vergeben" text="Lege ein neues Passwort fest, um dich anschließend direkt wieder anmelden zu können." />
    <section className="panel authPanel">
      <form onSubmit={submit}>
        <input required minLength="10" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Neues Passwort (mindestens 10 Zeichen)" />
        <input required type="password" value={repeat} onChange={e => setRepeat(e.target.value)} placeholder="Neues Passwort wiederholen" />
        <button>Passwort speichern</button>
      </form>
      {message && <p className="message">{message}</p>}
    </section>
  </Shell>;
}
function PanelSelection({ user, panels }){return <Shell><Profile user={user}/><Hero eyebrow="Meine Verkostungen" title="Panel auswählen" text="Hier erscheinen ausschließlich die Panels, denen du zugeordnet bist."/><section className="results">{panels.map(p=><article className="resultCard" key={p.id}><p className="eyebrow">Panel {p.slug}</p><h2>{p.title}</h2><p>{p.description}</p><button onClick={()=>{navigate(tastingUrl(p.slug));setLocationState({path: tastingUrl(p.slug), search: `?panel=${p.slug}`})}}>Verkostung öffnen</button></article>)}</section><Themen user={user} /></Shell>}
function AuthenticatedView({user,panel,path}){const[session,setSession]=useState(null);useEffect(()=>{api.session(panel).then(setSession).catch(()=>navigate('/gustumejo/'))},[panel]);if(!session)return <Shell><Profile user={user}/><Notice title="Panel wird geladen" text="Deine Berechtigung wird geprüft."/></Shell>;if(path.includes('/auswertung'))return <Shell><Profile user={user}/><Results session={session}/></Shell>;if(path.includes('/oel/'))return <Shell><Profile user={user}/><OilDetail session={session} oilId={decodeURIComponent(path.split('/oel/')[1]||'')}/></Shell>;return <Shell><Profile user={user}/><Tasting session={session} panel={panel} user={user}/></Shell>}

function navigate(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function Themen({ user }) {
  const sichtbar = themen.filter(thema => !thema.nurAdmin || user?.role === 'ADMIN');
  return <section className="chapter themen">
    <p className="eyebrow">Themen</p>
    <h2>Wissen zum Verkosten</h2>
    <div className="themenKacheln">
      {sichtbar.map(thema => <a key={thema.pfad} className="themaKachel" href={thema.pfad} onClick={event => { event.preventDefault(); navigate(thema.pfad); window.scrollTo(0, 0); }}>
        <span className="eyebrow">{thema.nurAdmin ? 'Einführung · nur für Admins' : 'Einführung'}</span>
        <strong>{thema.titel}</strong>
        <span className="themaText">{thema.text}</span>
        <span className="themaWeiter">Zur Einführung →</span>
      </a>)}
    </div>
  </section>;
}

function Shell({ children }) {
  return <main>{children}</main>;
}

// Kopf- und Fußzeile einmal um die ganze App, nicht pro Ansicht (Shell wird verschachtelt verwendet)
function Rahmen({ children }) {
  return <>
    <header className="appHeader">
      <a href="https://gustumas.de/" aria-label="Zur Website gustumas.de"><span className="appMarke">gustumas</span></a>
      <span className="appBereich"><strong>Gustumejo</strong><span> · Verkostung</span></span>
    </header>
    {children}
    <footer className="appFooter">
      <a href="https://gustumas.de/">gustumas.de</a>
      <a href="https://gustumas.de/impressum.html">Impressum</a>
      <a href="https://gustumas.de/datenschutz.html">Datenschutz</a>
    </footer>
  </>;
}

function Hero({ eyebrow, title, text, actions, compact = false }) {
  return <section className={compact ? 'hero compact' : 'hero'}>
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p className="heroText">{text}</p>
      <div className="actions">{actions}</div>
    </div>
  </section>;
}

const oxidationProcessCards = [
  {
    title: 'Mahlen: Biochemie beginnt',
    text: 'Beim Mahlen werden Zellkompartimente zerstört. Enzyme (β-Glucosidase, LOX, PPO, POD), Substrate und Sauerstoff treffen aufeinander und starten komplexe Reaktionen.',
    keyProcess: 'Zellzerstörung → enzymatische Reaktionen',
    enzymes: ['β-Glucosidase: Secoiridoid-Bildung (Oleacein, Oleocanthal)', 'Lipoxygenase (LOX): C5-/C6-Verbindungen für grüne Aromen', 'Polyphenoloxidase (PPO): Phenoloxidation', 'Peroxidase (POD): Phenolabbau'],
    aromaImpact: 'LOX-Produkte (Aldehyde, Alkohole, Ester) prägen typische Aromen von nativem Olivenöl extra: frisch geschnittenes Gras, grüne Blätter, Tomatenblatt, Artischocke, grüne Mandel.',
    kitchenRelevance: 'Enzymatische Lipidoxidation ist nicht automatisch Verderb – sie ist essenziell für die Aromabildung. Ein Teil der LOX-Aromabildung beginnt bereits beim Mahlen.',
    sources: [
      'Sauerstoff als technologischer Hilfsstoff beim Mahlen und Malaxieren (Foods 2023). <https://pmc.ncbi.nlm.nih.gov/articles/PMC10252381/>',
      'Einfluss von Sauerstoff während Crushing/Malaxation. <https://pubmed.ncbi.nlm.nih.gov/18510335/>',
      'Antioxidantien in griechischen nativen Olivenölen (Antioxidants 2014). <https://pmc.ncbi.nlm.nih.gov/articles/PMC4665486/>'
    ]
  },
  {
    title: 'Sauerstoff: Freund und Feind',
    text: 'Sauerstoff hat während der Verarbeitung gegensätzliche Wirkungen: Er fördert erwünschte LOX-Aromabildung, kann aber auch Phenole durch PPO/POD oxidieren.',
    keyProcess: 'LOX + O₂ → flüchtige Aromastoffe vs. Phenole + oxidative Enzyme + O₂ → Phenolverlust',
    tradeOffs: [
      { lessOxygen: 'bessere Phenolerhaltung', moreOxygen: 'stärkere Phenoloxidation' },
      { lessOxygen: 'geringere PPO/POD-Wirkung', moreOxygen: 'stärkere PPO/POD-Wirkung' },
      { lessOxygen: 'geringere LOX-Aromabildung', moreOxygen: 'stärkere LOX-Aromabildung' },
      { lessOxygen: 'tendenziell höhere spätere oxidative Stabilität', moreOxygen: 'potentiell geringere Stabilität' }
    ],
    conclusion: 'Die optimale Sauerstoffführung ist kein simples „so wenig wie möglich". Vollständiger Sauerstoffausschluss schützt Phenole, reduziert aber erwünschte LOX-Aromatik.',
    sources: [
      'Oxygen concentration during malaxation and phenolic composition. <https://pubmed.ncbi.nlm.nih.gov/18837502/>',
      'Controlled oxygen during olive-oil processing. <https://pubmed.ncbi.nlm.nih.gov/37297415/>',
      'Review zu Malaxationsparametern. <https://www.sciencedirect.com/science/article/pii/S2772753X22001514>'
    ]
  },
  {
    title: 'Malaxation: Koaleszenz mit Nebenwirkungen',
    text: 'Beim langsamen Kneten der Olivenpaste koaleszieren Öltröpfchen zu größeren Bereichen, die sich besser von Wasser und Feststoffen trennen lassen. Gleichzeitig laufen enzymatische und oxidative Vorgänge weiter.',
    keyProcess: 'Koaleszenz der Öltröpfchen → bessere Trennung',
    influencingFactors: ['Zeit', 'Temperatur', 'Sauerstoffkonzentration', 'Sorte', 'Reifegrad'],
    impact: 'Diese Faktoren beeinflussen nicht nur die Ausbeute, sondern auch Phenolgehalt und Aromaprofil. „Kalt extrahiert" beschreibt daher nur einen Teil der Qualitätsparameter.',
    sources: [
      'Verbindungen, die das sensorische Profil sortenreiner Olivenöle prägen (Molecules 2017). <https://pmc.ncbi.nlm.nih.gov/articles/PMC6150176/>',
      'Temperature and malaxation-time effects in Arbequina. <https://pmc.ncbi.nlm.nih.gov/articles/PMC8614922/>'
    ]
  },
  {
    title: 'Enzymatische vs. Autoxidation',
    text: 'Während Mahlen und Malaxation ist enzymatische Oxidation sensorisch erwünscht. Nach der Extraktion soll Sauerstoff dagegen vermieden werden, da nichtenzymatische radikalische Reaktionen zu Ranzigkeit führen.',
    keyProcess: 'PUFA + LOX + O₂ → erwünschte flüchtige Aromastoffe vs. ungesättigte Fettsäuren → Hydroperoxide → sekundäre Oxidationsprodukte → Ranzigkeit',
    distinction: 'Die erste Reaktion gehört zur Entstehung des typischen Aromas von nativem Olivenöl extra; die zweite ist Alterung und Qualitätsverlust.',
    sources: [
      'Einfluss von Malaxationssauerstoff und anschließender Lagerung. <https://pubmed.ncbi.nlm.nih.gov/25842336/>'
    ]
  }
];

const culinaryInsightCards = [
  {
    title: 'Phenole vs. LOX-Produkte',
    text: 'Die Unterscheidung zwischen Phenolen und LOX-Produkten ist für die Verwendung in der Küche besonders nützlich.',
    phenolCharacteristics: [
      'antioxidativ wirksam',
      'mit Bitterkeit und Schärfe verbunden',
      'wesentlich weniger flüchtig als LOX-Aromastoffe',
      'beim Erhitzen nicht stabil – werden teilweise verbraucht und umgewandelt'
    ],
    loxCharacteristics: [
      'flüchtige C5-/C6-Verbindungen',
      'verantwortlich für grüne, grasige, blättrige und fruchtige Aromatik',
      'gehen beim starken/längeren Erhitzen deutlich zurück'
    ],
    kitchenImplication: 'Phenolreiche Öle wirken wie Würzmittel und schützen andere Bestandteile vor Oxidation. LOX-Produkte prägen die frische Aromatik und sind hitzeempfindlich.',
    sources: [
      'Phenol supplementation and thermal oxidation at 180 °C. <https://pubmed.ncbi.nlm.nih.gov/25624222/>',
      'Frying stability of phenol-rich olive oils. <https://pubmed.ncbi.nlm.nih.gov/20678538/>',
      'Effect of sautéing temperature on EVOO polyphenols. <https://pubmed.ncbi.nlm.nih.gov/31963124/>',
      'Review zu EVOO beim Kochen. <https://pmc.ncbi.nlm.nih.gov/articles/PMC8838846/>',
      'Untersuchung thermischer Veränderungen des Volatilprofils. <https://pmc.ncbi.nlm.nih.gov/articles/PMC11545581/>'
    ]
  },
  {
    title: 'Küchenanwendungen im Vergleich',
    text: 'Statt Olivenöl lediglich nach „billig zum Braten" und „teuer für Salat" einzuteilen, lassen sich zwei unabhängige Qualitätsachsen betrachten.',
    axisA: {
      title: 'Phenolprofil',
      relevance: ['Bitterkeit und Schärfe', 'antioxidative Stabilität', 'Verhalten während thermischer Verarbeitung', 'ernährungsphysiologisch interessante Begleitstoffe']
    },
    axisB: {
      title: 'LOX-/Volatilprofil',
      relevance: ['grüne Fruchtigkeit', 'Gras- und Blattnoten', 'Tomaten- und Artischockenaromen', 'sensorische Komplexität', 'insbesondere rohe bzw. spät zugegebene Anwendungen']
    },
    oilTypes: [
      'phenolreich, aber aromatisch vergleichsweise zurückhaltend',
      'stark aromatisch, aber weniger phenolreich',
      'beide Eigenschaften in hoher Ausprägung kombiniert'
    ],
    applicationGuide: [
      { application: 'längeres Braten', properties: 'oxidative Stabilität, Ölsäure, Phenole' },
      { application: 'kurzes Sautieren', properties: 'Stabilität + teilweise nutzbare Aromatik' },
      { application: 'Marinieren', properties: 'Phenolprofil + passende Aromatik' },
      { application: 'Finishen', properties: 'Phenole + möglichst vollständiges Volatil-/LOX-Profil' }
    ],
    conclusion: 'Ein gutes, phenolreiches und stabiles natives Olivenöl extra kann ein sehr gutes Koch- und Bratöl sein. Ein besonders teures Öl mit herausragender LOX-Aromatik lässt sich sensorisch effizienter als Finish einsetzen.'
  }
];

const presentationChapters = [['grades', 'Güteklassen'], ['process', 'Herstellung'], ['oxidation', 'Oxidation'], ['costs', 'Kosten'], ['aroma', 'Aromen'], ['ingredients', 'Inhaltsstoffe'], ['culinary', 'Vertiefung'], ['kitchen', 'Küche'], ['history', 'Geschichte']];

function useActiveChapter(ids) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) setActive(visible.target.id);
    }, { rootMargin: '-15% 0px -75% 0px' });
    ids.forEach((id) => { const element = document.getElementById(id); if (element) observer.observe(element); });
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const nav = document.querySelector('.chapterNav');
    const chip = nav?.querySelector('.active');
    if (nav && chip && nav.scrollWidth > nav.clientWidth) nav.scrollTo({ left: chip.offsetLeft - (nav.clientWidth - chip.clientWidth) / 2, behavior: 'smooth' });
  }, [active]);
  return active;
}

function PresentationV2() {
  const [activeStep, setActiveStep] = useState(null);
  const [activeGrade, setActiveGrade] = useState(null);
  const [activeIngredient, setActiveIngredient] = useState(null);
  const [activeAroma, setActiveAroma] = useState(null);
  const [activeKitchen, setActiveKitchen] = useState(null);
  const [activeOxidation, setActiveOxidation] = useState(null);
  const [activeCulinary, setActiveCulinary] = useState(null);
  
  const activeChapter = useActiveChapter(presentationChapters.map(([id]) => `kapitel-${id}`));

  return <Shell>
    <Hero
      eyebrow="Olivenöl als Handwerk"
      title="Von der Olive zum Ausdruck auf dem Teller"
      text="Eine kompakte Einführung für Köche: Güteklassen, Verarbeitung, Kosten, Inhaltsstoffe und kulinarische Verwendung."
      actions={<button onClick={() => navigate('/gustumejo/')}>Zur Verkostung</button>}
    />
    <nav className="oilNav chapterNav" aria-label="Kapitel">
      {presentationChapters.map(([id, label]) => <button key={id} className={activeChapter === `kapitel-${id}` ? 'active' : ''} onClick={() => document.getElementById(`kapitel-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{label}</button>)}
    </nav>
    <section className="chapter dark" id="kapitel-grades">
      <p className="eyebrow">Güteklassen</p>
      <h2>Qualität beginnt bei Fehlerfreiheit, endet dort aber nicht.</h2>
      <div className="cards four">
        {gradeCards.map((grade) => <InfoCard key={grade.title} title={grade.title} text={grade.text} onClick={() => setActiveGrade(grade)} />)}
      </div>
    </section>
    
    <section className="chapter process" id="kapitel-process">
      <p className="eyebrow">Herstellung</p>
      <h2>Jede Stunde und jedes Grad Celsius kosten Aroma.</h2>
      <div className="timeline">
        {processSteps.map((item, index) => <button className="timelineStep" key={item.title} onClick={() => setActiveStep(item)}>
          <span>{index + 1}</span><strong>{item.title}</strong><small>{item.short}</small>
        </button>)}
      </div>
      <p className="lead">Frühe Ernte, schnelle Verarbeitung, kleine Chargen und niedrige Extraktionstemperaturen reduzieren Ausbeute und erhöhen Kosten pro Liter, schützen aber Frische, Polyphenole und Präzision.</p>
    </section>
    {activeStep && <ProcessModal step={activeStep} onClose={() => setActiveStep(null)} />}
    {activeGrade && <GradeModal grade={activeGrade} onClose={() => setActiveGrade(null)} />}
    
    <section className="chapter oxidation" id="kapitel-oxidation">
      <p className="eyebrow">Vertiefung: Oxidationsprozesse</p>
      <h2>Enzymatische Oxidation vs. Autoxidation: Der entscheidende Unterschied.</h2>
      <p className="lead">Während der Verarbeitung ist Sauerstoff sowohl Freund als auch Feind. Kontrollierte enzymatische Oxidation schafft Aromen; unkontrollierte Autoxidation zerstört sie.</p>
      <div className="cards four">
        {oxidationProcessCards.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => setActiveOxidation(item)} />)}
      </div>
    </section>
    {activeOxidation && <OxidationModal item={activeOxidation} onClose={() => setActiveOxidation(null)} />}
    
    <section className="chapter costs" id="kapitel-costs">
      <p className="eyebrow">Kosten pro Liter (Schätzung)</p>
      <h2>Preis entsteht aus Ausbeute, Kontrolle und Vertriebsweg.</h2>
      <p className="lead">Alle Kosten sind grobe Schätzungen ohne amtliche Quelle. Premiumöl ist hier als natives Olivenöl extra oberhalb der Health-Claim-Schwelle der VO (EU) 432/2012 (mindestens 250 mg/kg Hydroxytyrosol und Derivate) gefasst.</p>
      <div className="costCards">
        {costCards.map((item) => <article className="costCard" key={item.title}>
          <p className="eyebrow">{item.subtitle}</p>
          <h3>{item.title}</h3>
          <div className="costRows">
            <div><span>Herstellung</span><strong>{item.production}</strong></div>
            <div><span>Vermarktung</span><strong>{item.marketing}</strong></div>
            <div><span>Verkauf</span><strong>{item.retail}</strong></div>
          </div>
          <p>{item.note}</p>
        </article>)}
      </div>
      <MarketFacts />
    </section>
    
    <section className="chapter aroma" id="kapitel-aroma">
      <p className="eyebrow">Olfaktorische Prüfung</p>
      <h2>Aromen entstehen in Sekunden und gehen über Monate verloren.</h2>
      <p className="lead">Beim Riechen prüfen wir nicht nur, ob ein Öl angenehm duftet. Wir lesen Verarbeitung, Fruchtzustand, Lagerung und Alter aus flüchtigen Aromastoffen.</p>
      <div className="cards four">
        {aromaCards.map((aroma) => <InfoCard key={aroma.title} title={aroma.title} text={aroma.text} onClick={() => setActiveAroma(aroma)} />)}
      </div>
    </section>
    {activeAroma && <AromaModal aroma={activeAroma} onClose={() => setActiveAroma(null)} />}
    
    <section className="chapter light" id="kapitel-ingredients">
      <p className="eyebrow">Inhaltsstoffe</p>
      <h2>Sensorik und Chemie treffen sich bei Bitterkeit und Schärfe.</h2>
      <div className="cards four">
        {ingredientCards.map((ingredient) => <InfoCard key={ingredient.title} title={ingredient.title} text={ingredient.text} onClick={() => setActiveIngredient(ingredient)} />)}
      </div>
    </section>
    {activeIngredient && <IngredientModal ingredient={activeIngredient} onClose={() => setActiveIngredient(null)} />}
    
    <section className="chapter culinary-insights" id="kapitel-culinary">
      <p className="eyebrow">Kulinarische Vertiefung</p>
      <h2>Phenole und LOX-Produkte: Zwei Seiten derselben Medaille.</h2>
      <p className="lead">Die zentrale Erkenntnis: Oxidation ist bei Olivenöl kein einheitlicher Vorgang. Kontrollierte enzymatische Oxidation gehört zur Entstehung eines hochwertigen nativen Olivenöls; unkontrollierte Autoxidation nach der Herstellung führt dagegen zu dessen Alterung.</p>
      <div className="cards two">
        {culinaryInsightCards.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => setActiveCulinary(item)} />)}
      </div>
    </section>
    {activeCulinary && <CulinaryModal item={activeCulinary} onClose={() => setActiveCulinary(null)} />}
    
    <section className="chapter kitchen" id="kapitel-kitchen">
      <p className="eyebrow">Küche</p>
      <h2>Das beste Öl ist das passend eingesetzte Öl.</h2>
      <div className="cards four">
        {kitchenCards.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => setActiveKitchen(item)} />)}
      </div>
    </section>
    {activeKitchen && <KitchenModal item={activeKitchen} onClose={() => setActiveKitchen(null)} />}
    
    <section className="chapter history" id="kapitel-history">
      <p className="eyebrow">Historische Entwicklung</p>
      <h2>Von der Antike bis zur modernen Spitzenqualität</h2>
      <p className="lead">
        Die Olive und ihre mechanische Ölgewinnung sind Jahrtausende alt. Ein modernes natives Olivenöl extra der Spitzenklasse als gezielt gestaltetes biochemisches Produkt ist dagegen erstaunlich jung.
      </p>
      
      <div className="cards four">
        <InfoCard
          key="history-1"
          title="Antike – 18. Jh."
          text="Steinmühle + Presse + Dekantieren: Hochwertiges natives Öl war möglich, allerdings ohne präzise Kontrolle von Verarbeitungszeit, Sauerstoff, Temperatur und enzymatischen Reaktionen."
        />
        <InfoCard
          key="history-2"
          title="1833"
          text="Hydraulische Olivenpresse: Die von Diego de Alvear in Montilla angepasste Presse verbesserte Verarbeitungsgeschwindigkeit, Pressdruck und Reproduzierbarkeit."
        />
        <InfoCard
          key="history-3"
          title="ca. 1960"
          text="Metallcrusher + separater Malaxer + Zentrifugation: Kontinuierliche Verfahren trennten erstmals Zellaufschluss und enzymatische Reaktionsphase technisch voneinander."
        />
        <InfoCard
          key="history-4"
          title="1970er/80er"
          text="Drei-Phasen-Dekanter: Kontinuierliche industrielle Extraktion, aber mit Wasserzugabe, die den Übergang hydrophiler Phenole in die Wasserphase förderte."
        />
        <InfoCard
          key="history-5"
          title="Frühe 1990er"
          text="Zwei-Phasen-Dekanter: Deutlich weniger Wasser, mehr Phenole im Öl. Erste wichtige Schwelle: Grundarchitektur für moderne native Olivenöle extra der Spitzenklasse (Metallcrusher → Malaxer → Zwei-Phasen-Dekanter)."
        />
        <InfoCard
          key="history-6"
          title="Ende 1990er/2000er"
          text="Geschlossene Malaxer: Geringerer und besser kontrollierbarer Luftkontakt während der Malaxation. Der Sauerstoffeintrag wird damit erstmals zu einer steuerbaren Größe."
        />
        <InfoCard
          key="history-7"
          title="ca. 2005–2015"
          text="Inertgas + O₂-Messung/-Regelung: Sauerstoff als Prozessparameter. LOX + O₂ → erwünschte C5-/C6-Aromen, gleichzeitig PPO/POD + Phenole + O₂ → Phenoloxidation. Zweite wichtige Schwelle: Gezielte Steuerung von Phenolerhaltung und LOX-Aromatik."
        />
        <InfoCard
          key="history-8"
          title="Ab ca. 2020"
          text="Vakuum, PEF, Ultraschall: Neuere Entwicklungen wie Hochvakuum oder gepulste elektrische Felder reduzieren Sauerstoffexposition und beeinflussen die Phenolerhaltung."
        />
      </div>
      
      <p className="lead">
        Der entscheidende Fortschritt bestand darin, die kurze Phase nach dem Zerquetschen kontrollieren zu lernen: <strong>In den ersten Minuten nach dem Zellaufschluss entscheidet das Zusammenspiel von Enzymen, Sauerstoff, Temperatur und Zeit wesentlich darüber, wie viele Phenole erhalten bleiben und welche LOX-Aromastoffe entstehen.</strong>
      </p>
    </section>
  </Shell>;
}

function WeinModal({ eyebrow, title, lead, abschnitte, onClose }) {
  useModalClose(onClose);
  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="wein-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">{eyebrow}</p>
      <h2 id="wein-modal-title">{title}</h2>
      {lead && <p className="modalLead">{lead}</p>}
      <div className="modalGrid">
        {abschnitte.map(({ h, text, items }) => <div key={h}>
          <h3>{h}</h3>
          {text && <p>{text}</p>}
          {items && <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>}
        </div>)}
      </div>
    </article>
  </div>;
}

// Weinwissen analog zur Olivenöl-Einführung; nur für Admins erreichbar (siehe App)
function WeinWissen() {
  const [aktiv, setAktiv] = useState(null);
  const activeChapter = useActiveChapter(weinKapitel.map(([id]) => `kapitel-${id}`));
  const oeffne = (eyebrow, title, lead, abschnitte) => setAktiv({ eyebrow, title, lead, abschnitte });
  return <Shell>
    <Hero
      eyebrow="Wein als Handwerk"
      title="Von der Traube zum Glas"
      text="Eine kompakte Einführung für Köche: Qualitätsstufen, Herstellung, Aromen, Inhaltsstoffe, Fehler und kulinarische Verwendung. Vorerst nur für Admins sichtbar."
      actions={<button onClick={() => navigate('/gustumejo/')}>Zur Startseite</button>}
    />
    <nav className="oilNav chapterNav" aria-label="Kapitel">
      {weinKapitel.map(([id, label]) => <button key={id} className={activeChapter === `kapitel-${id}` ? 'active' : ''} onClick={() => document.getElementById(`kapitel-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{label}</button>)}
    </nav>
    <section className="chapter dark" id="kapitel-qualitaet">
      <p className="eyebrow">Qualitätsstufen</p>
      <h2>Die Stufe beschreibt Herkunft und Regeln, nicht automatisch den Geschmack.</h2>
      <div className="cards four">
        {qualitaetsKarten.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => oeffne('Qualitätsstufe', item.title, item.text, item.abschnitte)} />)}
      </div>
    </section>
    <section className="chapter process" id="kapitel-herstellung">
      <p className="eyebrow">Herstellung</p>
      <h2>Jede Entscheidung im Weinberg und im Keller bleibt im Glas.</h2>
      <div className="timeline">
        {herstellungsSchritte.map((item, index) => <button className="timelineStep" key={item.title} onClick={() => oeffne('Herstellungsschritt', item.title, item.short, [{ h: 'Reale Unterschiede', items: item.unterschiede }, { h: 'Kostenwirkung', text: item.kosten }, { h: 'Mögliche Fehler', items: item.fehler }])}>
          <span>{index + 1}</span><strong>{item.title}</strong><small>{item.short}</small>
        </button>)}
      </div>
      <p className="lead">Niedrige Erträge, Handlese, kühle Gärung und lange Reifung erhöhen den Aufwand je Flasche, schützen aber Frucht, Frische und Herkunftscharakter. Preis und Qualität hängen zusammen, sind aber nicht dasselbe.</p>
    </section>
    <section className="chapter aroma" id="kapitel-aromen">
      <p className="eyebrow">Olfaktorische Prüfung</p>
      <h2>Aromen erzählen Rebsorte, Gärung und Alter.</h2>
      <p className="lead">Beim Riechen wird nicht nur geprüft, ob ein Wein angenehm duftet. Man liest Traube und Herkunft (Primär), Gärung (Sekundär) und Ausbau oder Reife (Tertiär).</p>
      <div className="cards four">
        {aromenKarten.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => oeffne('Aromen', item.title, item.text, [{ h: 'Typische Stoffe und Noten', items: item.stoffe }, { h: 'Wie sie entstehen', text: item.entstehung }, { h: 'Wie sie verloren gehen', text: item.verlust }, { h: 'In der Praxis', text: item.praxis }])} />)}
      </div>
    </section>
    <section className="chapter light" id="kapitel-inhaltsstoffe">
      <p className="eyebrow">Inhaltsstoffe</p>
      <h2>Sensorik und Chemie treffen sich bei Säure, Süße und Gerbstoff.</h2>
      <div className="cards four">
        {inhaltsstoffKarten.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => oeffne('Inhaltsstoff', item.title, item.text, [{ h: 'Warum er wichtig ist', text: item.warum }, { h: 'Sensorik', text: item.sensorik }, { h: 'Küche', text: item.kueche }])} />)}
      </div>
    </section>
    <section className="chapter oxidation" id="kapitel-fehler">
      <p className="eyebrow">Fehler erkennen</p>
      <h2>Ein Fehler ist nicht schlechter Geschmack, sondern ein erkennbarer Defekt.</h2>
      <p className="lead">Wie beim Olivenöl gilt: Fehler getrennt von Stil wahrnehmen. Manche Noten sind in kleinen Mengen Teil der Komplexität, in größeren Mengen ein Defekt.</p>
      <div className="cards four">
        {fehlerKarten.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => oeffne('Weinfehler', item.title, item.text, [{ h: 'Stoffe und Merkmale', items: item.stoffe }, { h: 'Ursache', text: item.ursache }, { h: 'In der Praxis', text: item.praxis }])} />)}
      </div>
    </section>
    <section className="chapter kitchen" id="kapitel-kueche">
      <p className="eyebrow">Küche</p>
      <h2>Der beste Wein ist der passend eingesetzte Wein.</h2>
      <div className="cards four">
        {kuecheKarten.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} onClick={() => oeffne('Küche', item.title, item.beschreibung, [{ h: 'Kernregel', text: item.temperatur }, { h: 'Anwendung', items: item.punkte }])} />)}
      </div>
    </section>
    <section className="chapter history" id="kapitel-geschichte">
      <p className="eyebrow">Historische Entwicklung</p>
      <h2>Von den Tonkrügen im Kaukasus zur kontrollierten Kellertechnik</h2>
      <div className="cards four">
        {geschichtsKarten.map((item) => <InfoCard key={item.title} title={item.title} text={item.text} />)}
      </div>
    </section>
    {aktiv && <WeinModal {...aktiv} onClose={() => setAktiv(null)} />}
  </Shell>;
}

function OxidationModal({ item, onClose }) {
  useModalClose(onClose);
  
  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="oxidation-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Oxidationsprozess</p>
      <h2 id="oxidation-modal-title">{item.title}</h2>
      <p className="modalLead">{item.text}</p>
      
      {item.keyProcess && <div className="modalGrid">
        <div>
          <h3>Schlüsselprozess</h3>
          <p>{item.keyProcess}</p>
        </div>
      </div>}
      
      {item.enzymes && <div className="modalGrid">
        <div>
          <h3>Relevante Enzyme</h3>
          <ul>{item.enzymes.map((enzyme) => <li key={enzyme}>{enzyme}</li>)}</ul>
        </div>
      </div>}
      
      {item.aromaImpact && <div className="modalGrid">
        <div>
          <h3>Aromatische Auswirkungen</h3>
          <p>{item.aromaImpact}</p>
        </div>
      </div>}
      
      {item.kitchenRelevance && <div className="modalGrid">
        <div>
          <h3>Kulinarische Relevanz</h3>
          <p>{item.kitchenRelevance}</p>
        </div>
      </div>}
      
      {item.tradeOffs && <div className="modalGrid">
        <div>
          <h3>Zielkonflikte: Sauerstoff</h3>
          <div className="comparisonTable" role="table">
            <div className="comparisonRow comparisonHead" role="row">
              <div role="columnheader">Weniger Sauerstoff</div>
              <div role="columnheader">Mehr Sauerstoff</div>
            </div>
            {item.tradeOffs.map((tradeOff, index) => (
              <div className="comparisonRow" role="row" key={index}>
                <div role="cell">{tradeOff.lessOxygen}</div>
                <div role="cell">{tradeOff.moreOxygen}</div>
              </div>
            ))}
          </div>
        </div>
      </div>}
      
      {item.conclusion && <div className="modalGrid">
        <div>
          <h3>Fazit</h3>
          <p>{item.conclusion}</p>
        </div>
      </div>}
      
      {item.influencingFactors && <div className="modalGrid">
        <div>
          <h3>Einflussfaktoren</h3>
          <ul>{item.influencingFactors.map((factor) => <li key={factor}>{factor}</li>)}</ul>
        </div>
      </div>}
      
      {item.impact && <div className="modalGrid">
        <div>
          <h3>Auswirkungen</h3>
          <p>{item.impact}</p>
        </div>
      </div>}
      
      {item.distinction && <div className="modalGrid">
        <div>
          <h3>Unterschiede</h3>
          <p>{item.distinction}</p>
        </div>
      </div>}
      
      <div className="modalGrid">
        <div className="costBox">
          <h3>Quellen</h3>
          <ul>{item.sources.map((source) => <li key={source}><SourceLink source={source} /></li>)}</ul>
        </div>
      </div>
    </article>
  </div>;
}

function CulinaryModal({ item, onClose }) {
  useModalClose(onClose);
  
  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="culinary-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Kulinarische Erkenntnis</p>
      <h2 id="culinary-modal-title">{item.title}</h2>
      <p className="modalLead">{item.text}</p>
      
      {item.phenolCharacteristics && <div className="modalGrid">
        <div>
          <h3>Phenoleigenschaften</h3>
          <ul>{item.phenolCharacteristics.map((char) => <li key={char}>{char}</li>)}</ul>
        </div>
        <div>
          <h3>LOX-Eigenschaften</h3>
          <ul>{item.loxCharacteristics.map((char) => <li key={char}>{char}</li>)}</ul>
        </div>
      </div>}
      
      {item.kitchenImplication && <div className="modalGrid">
        <div>
          <h3>Kulinarische Bedeutung</h3>
          <p>{item.kitchenImplication}</p>
        </div>
      </div>}
      
      {item.axisA && item.axisB && <div className="modalGrid">
        <div>
          <h3>Qualitätsachse A: {item.axisA.title}</h3>
          <p>Relevant für:</p>
          <ul>{item.axisA.relevance.map((rel) => <li key={rel}>{rel}</li>)}</ul>
        </div>
        <div>
          <h3>Qualitätsachse B: {item.axisB.title}</h3>
          <p>Relevant für:</p>
          <ul>{item.axisB.relevance.map((rel) => <li key={rel}>{rel}</li>)}</ul>
        </div>
      </div>}
      
      {item.oilTypes && <div className="modalGrid">
        <div>
          <h3>Öl-Typen</h3>
          <p>Ein Öl kann:</p>
          <ul>{item.oilTypes.map((type) => <li key={type}>{type}</li>)}</ul>
        </div>
      </div>}
      
      {item.applicationGuide && <div className="modalGrid">
        <div>
          <h3>Anwendungsempfehlungen</h3>
          <div className="comparisonTable" role="table">
            <div className="comparisonRow comparisonHead" role="row">
              <div role="columnheader">Anwendung</div>
              <div role="columnheader">Besonders interessante Eigenschaften</div>
            </div>
            {item.applicationGuide.map((app, index) => (
              <div className="comparisonRow" role="row" key={index}>
                <div role="cell">{app.application}</div>
                <div role="cell">{app.properties}</div>
              </div>
            ))}
          </div>
        </div>
      </div>}
      
      {item.conclusion && <div className="modalGrid">
        <div>
          <h3>Fazit</h3>
          <p>{item.conclusion}</p>
        </div>
      </div>}
      
      {item.sources && item.sources.length > 0 && (
        <div className="modalGrid">
          <div className="costBox">
            <h3>Quellen</h3>
            <ul>{item.sources.map((source) => <li key={source}><SourceLink source={source} /></li>)}</ul>
          </div>
        </div>
      )}
    </article>
  </div>;
}

function KitchenModal({ item, onClose }) {
  useModalClose(onClose);
  
  if (!item) return null;
  

  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="kitchen-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Kücheneinsatz</p>
      <h2 id="kitchen-modal-title">{item.title}</h2>
      <p className="modalLead">{item.description}</p>
      <div className="modalGrid kitchenGrid">
        <div>
          <h3>Einsatzmöglichkeiten</h3>
          <ul>{item.uses.map((use) => <li key={use}>{use}</li>)}</ul>
        </div>
        <div>
          <h3>Was passiert im Öl?</h3>
          <p>{item.oilChanges}</p>
        </div>
        <div>
          <h3>Worauf achten?</h3>
          <p>{item.caution}</p>
        </div>
        <div className="costBox">
          <h3>Temperatur</h3>
          <p>{item.temperature}</p>
        </div>
      </div>
    </article>
  </div>;
}

function AromaModal({ aroma, onClose }) {
  useModalClose(onClose);

  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="aroma-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Olfaktorische Prüfung</p>
      <h2 id="aroma-modal-title">{aroma.title}</h2>
      <p className="modalLead">{aroma.text}</p>
      <div className="modalGrid aromaGrid">
        <div>
          <h3>Welche Stoffe riechen wir?</h3>
          <ul>{aroma.substances.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div>
          <h3>Wo werden sie herausgearbeitet?</h3>
          <p>{aroma.created}</p>
        </div>
        <div className="costBox">
          <h3>Wie gehen sie verloren?</h3>
          <p>{aroma.lost}</p>
        </div>
        <div>
          <h3>Prüfpraxis</h3>
          <p>{aroma.practice}</p>
        </div>
      </div>
    </article>
  </div>;
}

function IngredientModal({ ingredient, onClose }) {
  useModalClose(onClose);

  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="ingredient-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Inhaltsstoff</p>
      <h2 id="ingredient-modal-title">{ingredient.title}</h2>
      <p className="modalLead">{ingredient.text}</p>
      <div className="modalGrid gradeGrid">
        <div>
          <h3>Warum relevant?</h3>
          <p>{ingredient.whyItMatters}</p>
        </div>
        <div className="costBox">
          <h3>Premiumsignal</h3>
          <p>{ingredient.premiumSignal}</p>
        </div>
        <div>
          <h3>Küchenbezug</h3>
          <p>{ingredient.kitchen}</p>
        </div>
      </div>
      {ingredient.evidence && <div className="modalGrid">
        <div>
          <h3>Studienlage</h3>
          <p>{ingredient.evidence}</p>
        </div>
        <div className="costBox">
          <h3>Empfehlung aus der Forschung</h3>
          <p>{ingredient.recommendation}</p>
        </div>
        <div>
          <h3>Quellen</h3>
          <ul>{ingredient.sources.map((source) => <li key={source}><SourceLink source={source} /></li>)}</ul>
        </div>
      </div>}
      <IngredientComparisonTable activeLabel={ingredient.rowLabel} />
    </article>
  </div>;
}

// Erlaubt Zeilenumbrüche nach Schrägstrichen, z. B. in „Oleocanthal/Oleacein“.
function breakable(text) {
  return String(text).replaceAll('/', '/\u200B');
}

function useScrollIntoWrap(selector) {
  const ref = useRef(null);
  useEffect(() => {
    const wrap = ref.current;
    const target = wrap?.querySelector(selector);
    const sticky = wrap?.querySelector('.comparisonRow > div:first-child');
    if (wrap && target && sticky && wrap.scrollWidth > wrap.clientWidth) wrap.scrollLeft += target.getBoundingClientRect().left - sticky.getBoundingClientRect().right;
  }, [selector]);
  return ref;
}

function IngredientComparisonTable({ activeLabel }) {
  return <div className="comparisonWrap">
    <h3>Vergleich der Güteklassen</h3>
    <div className="comparisonTable" role="table" aria-label="Inhaltsstoffe nach Güteklasse">
      <div className="comparisonRow comparisonHead" role="row">
        <div role="columnheader">Merkmal</div>
        {gradeComparisonColumns.map((column) => <div role="columnheader" key={column}>{column}</div>)}
      </div>
      {gradeComparison.map(([label, ...values]) => <div className={`comparisonRow ${label === activeLabel ? 'activeRow' : ''}`} role="row" key={label}>
        <div role="cell">{breakable(label)}</div>
        {values.map((value, index) => <div role="cell" key={gradeComparisonColumns[index]}>{breakable(value)}</div>)}
      </div>)}
    </div>
  </div>;
}

function GradeModal({ grade, onClose }) {
  useModalClose(onClose);

  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="grade-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Güteklasse</p>
      <h2 id="grade-modal-title">{grade.title}</h2>
      <p className="modalLead">{grade.text}</p>
      <div className="modalGrid gradeGrid">
        <div>
          <h3>Merkmale</h3>
          <ul>{grade.components.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div className="costBox">
          <h3>{grade.title === 'Premiumöl' ? 'Definition von Premiumöl' : 'Differenz zum Premiumöl'}</h3>
          <p>{grade.premiumDifference}</p>
        </div>
        <div>
          <h3>Sensorischer Eindruck</h3>
          <div className="modalTags">{grade.sensory.map((item) => <span key={item}>{item}</span>)}</div>
        </div>
        <div>
          <h3>Küche</h3>
          <p>{grade.kitchen}</p>
        </div>
      </div>
      <GradeComparisonTable activeKey={grade.comparisonKey} />
    </article>
  </div>;
}

function GradeComparisonTable({ activeKey }) {
  const wrapRef = useScrollIntoWrap('.comparisonHead .activeColumn');
  return <div className="comparisonWrap" ref={wrapRef}>
    <h3>Inhaltsstoffe und Eigenschaften im Vergleich</h3>
    <div className="comparisonTable" role="table" aria-label="Vergleich der Güteklassen">
      <div className="comparisonRow comparisonHead" role="row">
        <div role="columnheader">Merkmal</div>
        {gradeComparisonColumns.map((column) => <div className={column === activeKey ? 'activeColumn' : ''} role="columnheader" key={column}>{column}</div>)}
      </div>
      {gradeComparison.map(([label, ...values]) => <div className="comparisonRow" role="row" key={label}>
        <div role="cell">{breakable(label)}</div>
        {values.map((value, index) => {
          const column = gradeComparisonColumns[index];
          return <div className={column === activeKey ? 'activeColumn' : ''} role="cell" key={column}>{breakable(value)}</div>;
        })}
      </div>)}
    </div>
  </div>;
}

function ProcessModal({ step, onClose }) {
  useModalClose(onClose);

  return <div className="modalBackdrop" role="presentation" onClick={onClose}>
    <article className="processModal" role="dialog" aria-modal="true" aria-labelledby="process-modal-title" onClick={(event) => event.stopPropagation()}>
      <button className="closeButton" aria-label="Popup schließen" onClick={onClose}>Schließen</button>
      <p className="eyebrow">Herstellungsschritt</p>
      <h2 id="process-modal-title">{step.title}</h2>
      <p className="modalLead">{step.short}</p>
      <div className="modalGrid">
        <div>
          <h3>Reale Unterschiede</h3>
          <ul>{step.differences.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div className="costBox">
          <h3>Kostenwirkung pro Liter (Schätzung)</h3>
          <p>{step.cost}</p>
        </div>
        <div>
          <h3>Mögliche Fehlnoten</h3>
          <ul>{step.faults.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </div>
    </article>
  </div>;
}

function useModalClose(onClose) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.classList.add('modalOpen');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('modalOpen');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);
}

// Verkostung als Rangfolge: erst alle Geruchsproben, dann die Geschmacksproben. Pro Kriterium werden alle Öle direkt
// verglichen und mit einem Regler von 0 (nicht wahrnehmbar) bis Anzahl der Öle (am intensivsten) eingeordnet.
function kriteriumSchluessel(text) {
  return text.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const phasenVorlage = [
  { key: 'geruch', titel: 'Geruch', einleitung: 'Rieche an allen Ölen, bevor du eines davon kostest. Die Geschmacksprobe folgt, wenn alle Geruchskriterien erfasst sind.' },
  { key: 'geschmack', titel: 'Geschmack', einleitung: 'Jetzt kostest du die Öle. Vergleiche sie wieder Kriterium für Kriterium direkt miteinander.' }
];
const stufenNamen = { einsteiger: 'Einsteiger', intermediate: 'Intermediate', profi: 'Profi' };

// Eigene Eindrücke: dritter Bereich, jederzeit erreichbar und freiwillig
const eindruckFelder = [
  { key: 'eindruck', label: 'Eigene Eindrücke' },
  { key: 'lernnotiz', label: 'Was nehme ich aus diesem Öl fachlich mit?' }
];

// Kriterien kommen pro Panel vom Server (Produkt, Kriterienauswahl des Panels)
function aktivePhasen(definitionen = []) {
  return phasenVorlage.map((phase) => ({ ...phase, kriterien: definitionen.filter((k) => k.phase === phase.key).map((k) => ({ key: k.schluessel, label: k.label, gruppe: k.gruppe })) }));
}

function kriteriumErfasst(werte, kriterium, oils) {
  const eintrag = werte[kriterium.key];
  return Boolean(eintrag) && oils.every((oil) => Number.isInteger(eintrag[oil.id]));
}

function Tasting({ session, panel }) {
  const oils = session.oils;
  const max = oils.length;
  const [geruch, geschmack] = aktivePhasen(session.kriterienDefinitionen);
  const [ranking, setRanking] = useState(null);
  const [bereich, setBereich] = useState('proben');
  const [schritt, setSchritt] = useState(0);
  const [entwurf, setEntwurf] = useState({});
  const [geaendert, setGeaendert] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [fehler, setFehler] = useState('');
  const karteRef = useRef(null);

  const werte = ranking?.werte || {};
  const geruchFertig = geruch.kriterien.every((k) => kriteriumErfasst(werte, k, oils));
  const geschmackFertig = geschmack.kriterien.every((k) => kriteriumErfasst(werte, k, oils));
  const phase = geruchFertig ? geschmack : geruch;
  const fertig = geruchFertig && geschmackFertig;
  const kriterium = bereich === 'proben' ? phase.kriterien[schritt] : null;

  function ersterOffenerSchritt(gespeichert) {
    const p = geruch.kriterien.every((k) => kriteriumErfasst(gespeichert, k, oils)) ? geschmack : geruch;
    const offen = p.kriterien.findIndex((k) => !kriteriumErfasst(gespeichert, k, oils));
    return offen === -1 ? p.kriterien.length : offen;
  }

  useEffect(() => {
    api.ranking(panel).then((geladen) => {
      const neu = { werte: geladen.werte || {}, texte: geladen.texte || {} };
      setRanking(neu);
      setSchritt(ersterOffenerSchritt(neu.werte));
    }).catch((reason) => setFehler(reason.message));
  }, [panel]);

  // Entwurf des aktuellen Schritts bzw. der Eindrücke aus dem Gespeicherten übernehmen
  useEffect(() => {
    if (!ranking) return;
    if (bereich === 'eindruecke') {
      setEntwurf(Object.fromEntries(eindruckFelder.map((feld) => [feld.key, { ...(ranking.texte[feld.key] || {}) }])));
    } else if (kriterium) {
      const gespeichert = ranking.werte[kriterium.key] || {};
      setEntwurf(Object.fromEntries(oils.map((oil) => [oil.id, Number.isInteger(gespeichert[oil.id]) ? gespeichert[oil.id] : 0])));
    }
    setGeaendert(false);
  }, [ranking, bereich, kriterium?.key]);

  useEffect(() => {
    if (!geaendert) return;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [geaendert]);

  if (fehler) return <Notice title="Verkostung" text={`Deine Eingaben konnten nicht geladen werden: ${fehler}`} />;
  if (!oils.length) {
    return <>
      <Hero eyebrow="Verkostung" title={session.title} text="Dieses Panel ist dir bereits zugeordnet, enthält aber aktuell noch keine Öle." actions={<button onClick={() => navigate('/gustumejo/')}>Zur Panelübersicht</button>} />
      <section className="panel login"><label>Status</label><p>Sobald Öle zugeordnet wurden, kannst du hier direkt mit der Verkostung starten.</p></section>
    </>;
  }
  if (!ranking) return <Notice title="Verkostung" text="Deine bisherigen Eingaben werden geladen." />;

  function wechsle(naechsterBereich, naechsterSchritt) {
    if (geaendert && !window.confirm('Es gibt ungespeicherte Eingaben. Trotzdem wechseln?')) return;
    setBereich(naechsterBereich);
    setSchritt(naechsterSchritt);
    setMessage('');
    karteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Nur vollständige, im Panel vorgesehene Kriterien mitschicken
  function bereinigt(alleWerte) {
    const aktiv = [...geruch.kriterien, ...geschmack.kriterien];
    return Object.fromEntries(Object.entries(alleWerte).filter(([key]) => aktiv.some((k) => k.key === key && kriteriumErfasst(alleWerte, k, oils))));
  }

  async function speichern() {
    setSaving(true);
    setMessage('');
    const naechste = bereich === 'eindruecke'
      ? { werte: bereinigt(ranking.werte), texte: { ...ranking.texte, ...entwurf } }
      : { werte: { ...bereinigt(ranking.werte), [kriterium.key]: entwurf }, texte: ranking.texte };
    try {
      const gespeichert = await api.saveRanking(panel, naechste);
      const neu = { werte: gespeichert.werte || {}, texte: gespeichert.texte || {} };
      const istGeruchFertig = geruch.kriterien.every((k) => kriteriumErfasst(neu.werte, k, oils));
      setRanking(neu);
      setGeaendert(false);
      if (bereich === 'eindruecke') {
        setMessage('Eindrücke gespeichert.');
      } else if (!geruchFertig && istGeruchFertig) {
        setSchritt(0);
        setMessage(geschmack.kriterien.length ? 'Geruchsprobe abgeschlossen. Jetzt folgt die Geschmacksprobe.' : 'Alle Kriterien erfasst.');
      } else {
        setSchritt(schritt + 1);
      }
      karteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      setMessage(`Nicht gespeichert: ${error.message.replace(/\.$/, '')}. Deine Eingaben sind noch da, bitte erneut speichern.`);
    } finally {
      setSaving(false);
    }
  }

  const reihenfolge = kriterium
    ? oils.map((oil, index) => ({ oil, nummer: probeKennung(session, index), index, wert: entwurf[oil.id] ?? 0 })).sort((a, b) => b.wert - a.wert || a.index - b.index)
    : [];
  const bereiche = [
    { key: 'geruch', titel: geruch.titel, nummer: 1, erledigt: geruchFertig, aktiv: bereich === 'proben' && !geruchFertig, offen: !geruchFertig, ziel: () => wechsle('proben', ersterOffenerSchritt(werte)) },
    { key: 'geschmack', titel: geschmack.titel, nummer: 2, erledigt: geruchFertig && geschmackFertig, aktiv: bereich === 'proben' && geruchFertig, offen: geruchFertig, ziel: () => wechsle('proben', ersterOffenerSchritt(werte)) },
    { key: 'eindruecke', titel: 'Eindrücke', nummer: 3, erledigt: false, aktiv: bereich === 'eindruecke', offen: true, ziel: () => wechsle('eindruecke', 0) }
  ];

  return <>
    <Hero
      compact
      eyebrow="Verkostung"
      title={session.title}
      text="Vergleiche die Öle direkt miteinander und ordne sie nach der Intensität jedes Kriteriums. Es geht um sensorisches Lernen, nicht um Gewinner oder Verlierer."
      actions={<><button className="secondary" onClick={() => navigate('/gustumejo/olivenoel/')}>Präsentation</button><button className="secondary" onClick={() => navigate(tastingUrl(panel, 'auswertung'))}>Auswertung</button></>}
    />
    <section className="verkostung">
      <ol className="phasen" aria-label="Ablauf">
        {bereiche.map((b) => <li key={b.key}>
          <button type="button" className={[b.aktiv ? 'aktiv' : '', b.erledigt ? 'erledigt' : '', b.offen ? '' : 'gesperrt'].join(' ').trim()} disabled={!b.offen || b.aktiv} aria-current={b.aktiv ? 'step' : undefined} onClick={b.ziel}>
            <span>{b.nummer}</span>{b.titel}{b.erledigt && ' ✓'}{b.key === 'eindruecke' && <small>optional</small>}
          </button>
        </li>)}
      </ol>
      {bereich === 'proben' && <>
        <p className="phasenText">{fertig ? 'Du hast alle Kriterien erfasst. Die Geschmackskriterien kannst du weiter anpassen.' : `${geruchFertig && schritt === 0 ? 'Die Geruchsprobe ist abgeschlossen. ' : ''}${phase.einleitung}`}</p>
        <nav className="schrittNav" aria-label={`Kriterien ${phase.titel}`}>
          {phase.kriterien.map((k, index) => <button key={k.key} className={index === schritt ? 'active' : ''} aria-current={index === schritt ? 'step' : undefined} onClick={() => wechsle('proben', index)}>
            {k.label}{kriteriumErfasst(werte, k, oils) && <span className="oilState" aria-label="erfasst">✓</span>}
          </button>)}
        </nav>
      </>}
      {bereich === 'eindruecke' && <p className="phasenText">Hier kannst du jederzeit festhalten, was dir an den einzelnen Ölen auffällt. Alle Felder sind freiwillig.</p>}
      <article className="formCard schrittKarte" ref={karteRef}>
        {bereich === 'eindruecke' ? <>
          <p className="eyebrow">Eindrücke · optional</p>
          <h2>Eigene Eindrücke</h2>
          {oils.map((oil, index) => <div className="notizOel" key={oil.id}>
            <h3><span className="probe">{probeKennung(session, index)}</span>{!istVerdeckt(session) && oil.name}</h3>
            {eindruckFelder.map((feld) => <textarea key={feld.key} aria-label={`${feld.label}: ${oil.name}`} placeholder={feld.label} value={entwurf[feld.key]?.[oil.id] || ''} onChange={(event) => { setEntwurf({ ...entwurf, [feld.key]: { ...(entwurf[feld.key] || {}), [oil.id]: event.target.value } }); setGeaendert(true); }} />)}
          </div>)}
          <div className="tastingActions">
            {message && <p className="message" role="status">{message}</p>}
            <div className="actions">
              <button disabled={saving} onClick={speichern}>Speichern</button>
              {!fertig && <button className="secondary" onClick={() => wechsle('proben', ersterOffenerSchritt(werte))}>Zurück zur Verkostung</button>}
              {fertig && <button className="secondary" onClick={() => navigate(tastingUrl(panel, 'auswertung'))}>Zur Auswertung</button>}
            </div>
          </div>
        </> : kriterium ? <>
          <p className="eyebrow">{[phase.titel, kriterium.gruppe].filter((teil, index, alle) => alle.indexOf(teil) === index).join(' · ')} · Schritt {schritt + 1} von {phase.kriterien.length}</p>
          <h2>{kriterium.label}</h2>
          <p className="schrittHinweis">Vergleiche alle Öle direkt miteinander. 0 = nicht wahrnehmbar, {max} = am intensivsten. Gleiche Werte sind erlaubt, wenn du keinen Unterschied wahrnimmst.</p>
          <ul className="rangListe">
            {oils.map((oil, index) => <li key={oil.id} className="rangZeile">
              <label htmlFor={`regler-${oil.id}`}><span className="probe">{probeKennung(session, index)}</span>{!istVerdeckt(session) && oil.name}</label>
              <input id={`regler-${oil.id}`} type="range" min="0" max={max} step="1" value={entwurf[oil.id] ?? 0} onChange={(event) => { setEntwurf({ ...entwurf, [oil.id]: Number(event.target.value) }); setGeaendert(true); }} aria-valuetext={`${entwurf[oil.id] ?? 0} von ${max}`} />
              <output htmlFor={`regler-${oil.id}`}>{entwurf[oil.id] ?? 0}</output>
            </li>)}
          </ul>
          <p className="reihenfolge"><strong>Deine Reihenfolge:</strong> {reihenfolge.map((r, index) => <React.Fragment key={r.oil.id}>{index > 0 && (reihenfolge[index - 1].wert === r.wert ? ' = ' : ' > ')}<span title={istVerdeckt(session) ? undefined : r.oil.name}>{r.nummer}</span></React.Fragment>)}</p>
          <div className="tastingActions">
            {message && <p className="message" role="status">{message}</p>}
            <div className="actions">
              {schritt > 0 && <button className="secondary" disabled={saving} onClick={() => wechsle('proben', schritt - 1)}>Zurück</button>}
              <button disabled={saving} onClick={speichern}>Speichern & weiter</button>
            </div>
          </div>
        </> : <>
          <p className="eyebrow">Verkostung</p>
          <h2>{fertig ? 'Alle Kriterien erfasst' : 'Geschafft'}</h2>
          <p className="schrittHinweis">{message || 'Danke! Du kannst jetzt noch deine Eindrücke zu den einzelnen Ölen festhalten oder dir die gemeinsame Auswertung ansehen.'}</p>
          <div className="actions">
            <button onClick={() => wechsle('eindruecke', 0)}>Eindrücke erfassen</button>
            <button className="secondary" onClick={() => navigate(tastingUrl(panel, 'auswertung'))}>Zur Auswertung</button>
          </div>
        </>}
      </article>
    </section>
  </>;
}

function Admin({ user }) {
  const [tab, setTab] = useState('Panel');
  const [panels, setPanels] = useState([]);
  const [users, setUsers] = useState([]);
  const [oils, setOils] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('001');
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedOils, setAssignedOils] = useState([]);
  const [message, setMessage] = useState(''); const [settings, setSettings] = useState({ aiApiUrl: '', aiModel: 'gpt-4.1-mini', aiApiKey: '', mailEnabled: false });
  const [modal, setModal] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [produkte, setProdukte] = useState([]);
  const istAdmin = user.role === 'ADMIN';
  const tabs = istAdmin ? ['Panel', 'Benutzer', 'Öle', 'Produkte', 'Konfiguration'] : ['Panel', 'Öle'];

  async function refresh() {
    try {
      const [nextPanels, nextUsers, nextOils, nextProdukte] = await Promise.all([api.adminPanels(), api.adminUsers(), api.adminOils(), api.produkte()]);
      setPanels(nextPanels); setUsers(nextUsers); setOils(nextOils); setProdukte(nextProdukte);
      // Organisatoren sehen nur ihre Panels: ggf. auf das erste eigene Panel wechseln
      if (!nextPanels.some((panel) => panel.slug === selectedSlug)) { setAssignedUsers([]); setAssignedOils([]); if (nextPanels[0]) setSelectedSlug(nextPanels[0].slug); return; }
      const [nextAssignedUsers, nextAssignedOils] = await Promise.all([api.panelUsers(selectedSlug), api.panelOils(selectedSlug)]);
      setAssignedUsers(nextAssignedUsers); setAssignedOils(nextAssignedOils);
    } catch (error) { setMessage(error.message); }
  }

  useEffect(() => { refresh(); }, [selectedSlug]);

  async function createPanel(event) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { const panel = await api.createPanel({ slug: form.get('slug'), title: form.get('title'), tastingDate: form.get('tastingDate'), description: form.get('description'), produkt: form.get('produkt') || 'olivenoel' }); setSelectedSlug(panel.slug); setModal(null); await refresh(); } catch (error) { setMessage(error.message); }
  }

  async function createUser(event) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api.createUser({ name: form.get('name'), username: form.get('username'), email: form.get('email'), role: form.get('role') || 'PARTICIPANT' }); setModal(null); setMessage(''); await refresh(); } catch (error) { setMessage(error.message); }
  }

  async function createOil(event) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api.createOil({ name: form.get('name'), producer: form.get('producer'), region: form.get('region'), cultivar: form.get('cultivar'), description: form.get('description') }); setModal(null); await refresh(); } catch (error) { setMessage(error.message); }
  }

  async function saveOil(event) { event.preventDefault(); const form = new FormData(event.currentTarget); const data = { name: form.get('name'), producer: form.get('producer'), region: form.get('region'), country: form.get('country') || 'Italien', cultivar: form.get('cultivar'), grade: form.get('grade') || 'Natives Olivenöl extra', category: form.get('category') || 'Premiumöl', description: form.get('description'), ingredientsNotes: form.get('ingredientsNotes'), kitchenUsage: form.get('kitchenUsage'), sortOrder: 0 }; try { if (modal?.item) await api.updateOil(modal.item.id, data); else await api.createOil(data); setModal(null); await refresh(); } catch (error) { setMessage(error.message); } }
  async function researchOil(event) { const form = new FormData(event.currentTarget.closest('form')); const image = modal.imageResearch; try { const result = await api.researchOil({ name: form.get('name'), producer: form.get('producer'), region: form.get('region'), labelText: image?.image?.extractedText || '', evidence: (image?.sources || []).filter(source => source.verified).map(source => ({ title: source.title, url: source.url, excerpt: source.excerpt })) }); setModal({ ...modal, research: result, item: { ...(modal?.item || {}), name: form.get('name'), producer: result.producerName || form.get('producer'), region: result.originRegion || form.get('region'), country: result.originCountry || 'Italien', cultivar: result.cultivars?.map(item => item.name).join(', ') || form.get('cultivar'), description: result.description, ingredientsNotes: result.ingredientsNotes, kitchenUsage: result.kitchenUsage } }); setMessage('Quellengebundener KI-Vorschlag geladen. Angaben vor Übernahme prüfen.'); } catch (error) { setMessage(error.message); } }
  async function applyResearch() { try { const saved = await api.applyResearch(modal.item.id, modal.research); setModal({ type: 'oilSheet', item: saved, edit: false }); setMessage('Geprüfte KI-Recherche in Stammdaten übernommen.'); await refresh(); } catch (error) { setMessage(error.message); } }
  async function researchImage(kind, file) { if (!file) return; setModal({ ...modal, imageStatus: 'Bildsuche läuft' }); try { const result = await imageResearch(kind, file, modal.item?.id); setModal({ ...modal, imageStatus: 'Bildsuche abgeschlossen', imageResearch: result, item: { ...(modal.item || {}), name: modal.item?.name || result.searchQuery } }); setMessage('Bild ausgewertet. Prüfe den extrahierten Text und die verifizierten Quellen.'); } catch (error) { setModal({ ...modal, imageStatus: 'Bildsuche fehlgeschlagen' }); setMessage(error.message); } }
  async function saveUser(event) { event.preventDefault(); const form = new FormData(event.currentTarget); try { await api.updateUser(modal.item.id, { name: form.get('name'), username: form.get('username'), email: form.get('email'), role: form.get('role'), active: form.get('active') === 'true' }); setModal(null); setMessage(''); await refresh(); } catch (error) { setMessage(error.message); } }
  async function loadSettings() { try { const result = await api.settings(); setSettings({ ...result, aiApiKey: '' }); } catch (error) { setMessage(error.message); } }
  async function saveSettings(event) { event.preventDefault(); try { await api.updateSettings(settings); setMessage('Konfiguration gespeichert. API-Key wird aus Sicherheitsgründen nicht angezeigt.'); await loadSettings(); } catch (error) { setMessage(error.message); } }
  async function sendTestMail() { try { const result = await api.sendTestMail(); setMessage(`Testmail versendet an ${result.recipient}.`); } catch (error) { setMessage(error.message); } }

  async function assignUser(user) {
    try { await api.assignUser(selectedSlug, user.id); await refresh(); await showInvitation(user); } catch (error) { setMessage(error.message); }
  }

  async function showInvitation(user) {
    try { const result = await api.invitation(user.id, selectedSlug); setInvitation({ user, token: result.token, panel: selectedSlug, mailSent: result.mailSent }); } catch (error) { setMessage(error.message); }
  }

  // Teilnehmer für das Panel einladen: neues Konto anlegen oder vorhandenes (gleiche Mail/Benutzername) zuordnen
  async function addTeilnehmer(event) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { const teilnehmer = await api.addTeilnehmer(selectedSlug, { name: form.get('name'), email: form.get('email'), username: form.get('username') }); setModal(null); await refresh(); await showInvitation(teilnehmer); } catch (error) { setMessage(error.message); }
  }

  async function createProdukt(event) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { const produkt = await api.createProdukt({ name: form.get('name'), schluessel: form.get('schluessel') || '' }); setModal(null); navigate(`/gustumejo/admin/produkt/${produkt.schluessel}`); } catch (error) { setMessage(error.message); }
  }

  async function assignOil(oil) {
    try { await api.assignOil(selectedSlug, oil.id, assignedOils.length + 1); await refresh(); } catch (error) { setMessage(error.message); }
  }

  async function updateDate(tastingDate) {
    try { await api.updatePanelDate(selectedSlug, tastingDate); await refresh(); } catch (error) { setMessage(error.message); }
  }

  const selected = panels.find((panel) => panel.slug === selectedSlug);
  return <Shell>
    <Profile user={user} />
    <Hero compact eyebrow="Verwaltung" title="Panel organisieren" text={istAdmin ? 'Zentralen Ölbestand, Benutzer und Produkte verwalten, dann gezielt einem Panel zuordnen.' : 'Deine Panels organisieren: Öle zuordnen, Kriterien wählen und Teilnehmende einladen.'} />
    <section className="adminArea">
      <div className="adminTabs">{tabs.map((item) => <button key={item} className={tab === item ? 'active' : ''} onClick={() => { setTab(item); if (item === 'Konfiguration') loadSettings(); }}>{item}</button>)}</div>
      {tab === 'Panel' && <>
        <div className="panelToolbar">
          <label>Panel<select value={selectedSlug} onChange={(event) => setSelectedSlug(event.target.value)}>{!panels.length && <option value="">Noch kein Panel</option>}{panels.map((panel) => <option key={panel.id} value={panel.slug}>{panel.slug} - {panel.title}</option>)}</select></label>
          <label>Datum<input type="date" value={selected?.tastingDate || ''} onChange={(event) => updateDate(event.target.value)} /></label>
          <button onClick={() => setModal('panel')}>Neues Panel</button>
        </div>
        <div className="adminGrid">
          <section className="formCard"><div className="cardHeading"><h2>Öle</h2><button className="secondary compactButton" onClick={() => setModal({ type: 'oilSheet', edit: true })}>Neues Öl</button></div>{oils.map((oil) => <AdminRow key={oil.id} title={oil.name} meta={oil.producer || oil.region} status={assignedOils.includes(oil.id) ? 'im Panel' : null}><button className="secondary compactButton" onClick={() => setModal({ type: 'oilSheet', item: oil, edit: false })}>Steckbrief</button>{!assignedOils.includes(oil.id) && <button className="secondary compactButton" onClick={() => assignOil(oil)}>Zuordnen</button>}</AdminRow>)}</section>
          <section className="formCard"><div className="cardHeading"><h2>{istAdmin ? 'Benutzer' : 'Teilnehmende'}</h2><button className="secondary compactButton" disabled={!selected} onClick={() => setModal({ type: 'teilnehmer' })}>Teilnehmer einladen</button></div>{!users.length && <p className="configHint">Noch keine Teilnehmenden.</p>}{users.map((entry) => { const assigned = assignedUsers.includes(entry.id); return <AdminRow key={entry.id} title={entry.name} meta={entry.email} status={assigned ? (entry.activated ? 'im Panel · aktiviert' : 'im Panel · Einladung offen') : null}>{istAdmin && <button className="secondary compactButton" onClick={() => setModal({ type: 'user', item: entry })}>Bearbeiten</button>}{!assigned && <button className="secondary compactButton" onClick={() => assignUser(entry)}>Zuordnen & Einladung</button>}{assigned && !entry.activated && <button className="secondary compactButton" onClick={() => showInvitation(entry)}>Einladung / QR</button>}</AdminRow>; })}</section>
        </div>
        {selected && <BlindProbe key={`blind-${selected.slug}`} panel={selected} oils={assignedOils.map((id) => oils.find((oil) => oil.id === id)).filter(Boolean)} onSaved={refresh} setMessage={setMessage} />}
        {selected && <PanelKriterien key={selected.slug} panel={selected} onSaved={refresh} setMessage={setMessage} />}
      </>}
      {tab === 'Benutzer' && <section className="formCard"><div className="cardHeading"><h2>Benutzer</h2><button className="secondary compactButton" onClick={() => setModal({ type: 'user' })}>Neuer Benutzer</button></div>{users.map((entry) => <AdminRow key={entry.id} title={entry.username ? `${entry.name} · ${entry.username}` : entry.name} meta={entry.email} status={`${entry.role === 'ADMIN' ? 'Admin' : 'Teilnehmer'} · ${entry.activated ? 'aktiviert' : 'Einladung erforderlich'}${entry.active ? '' : ' · deaktiviert'}`}><button className="secondary compactButton" onClick={() => setModal({ type: 'user', item: entry })}>Bearbeiten</button></AdminRow>)}</section>}
      {tab === 'Öle' && <section className="formCard"><div className="cardHeading"><h2>Zentraler Ölbestand</h2><button className="secondary compactButton" onClick={() => setModal({ type: 'oilSheet', edit: true })}>Neues Öl</button></div>{oils.map((oil) => <AdminRow key={oil.id} title={oil.name} meta={[oil.producer, oil.region, oil.cultivar].filter(Boolean).join(' · ') || 'Stammdaten ergänzen'} status={[oil.category, oil.grade].filter(Boolean).join(' · ')}><button className="secondary compactButton" onClick={() => setModal({ type: 'oilSheet', item: oil, edit: false })}>Steckbrief</button></AdminRow>)}</section>}
      {tab === 'Produkte' && <section className="formCard"><div className="cardHeading"><h2>Produkte</h2><button className="secondary compactButton" onClick={() => setModal({ type: 'produkt' })}>Neues Produkt</button></div><p className="configHint">Je Produkt legst du die Verkostungskriterien und die Stufen Einsteiger, Intermediate und Profi fest; eigene Kriterien sind möglich.</p>{produkte.map((produkt) => <AdminRow key={produkt.schluessel} title={produkt.name} meta={produkt.schluessel}><button className="secondary compactButton" onClick={() => navigate(`/gustumejo/admin/produkt/${produkt.schluessel}`)}>Kriterien & Stufen</button></AdminRow>)}</section>}
      {tab === 'Konfiguration' && <section className="formCard"><h2>Konfiguration</h2><form className="adminForm" onSubmit={saveSettings}><input value={settings.aiApiUrl || ''} onChange={e => setSettings({ ...settings, aiApiUrl: e.target.value })} placeholder="OpenAI-kompatible API-URL"/><input value={settings.aiModel || ''} onChange={e => setSettings({ ...settings, aiModel: e.target.value })} placeholder="Modell"/><input type="password" value={settings.aiApiKey} onChange={e => setSettings({ ...settings, aiApiKey: e.target.value })} placeholder={settings.aiApiKeyConfigured ? 'API-Key ist gesetzt, hier nur zum Ersetzen eingeben' : 'OpenAI API-Key'}/><label className="checkLabel"><input type="checkbox" checked={settings.mailEnabled} onChange={e => setSettings({ ...settings, mailEnabled: e.target.checked })}/> Mailversand aktivieren</label><p className="configHint">Quality und Production senden über den internen Relay `10.23.1.2:25` ohne SMTP-Login. Der Absender ist `support@baredev.de`.</p><div className="actions"><button type="button" className="secondary" onClick={sendTestMail}>Testmail senden</button><button>Konfiguration speichern</button></div></form></section>}
      {message && <div className="adminToast" role="status"><p className="message">{message}</p><button className="secondary compactButton" aria-label="Meldung schließen" onClick={() => setMessage('')}>×</button></div>}
    </section>
    {invitation && <AdminModal title="Einladung" onClose={() => setInvitation(null)}><InvitationView invitation={invitation} /></AdminModal>}
    {modal === 'panel' && <AdminModal title="Neues Panel" onClose={() => setModal(null)}><form onSubmit={createPanel}><input required name="slug" placeholder="Kennung" /><input required name="title" placeholder="Titel" /><input required type="date" name="tastingDate" /><textarea name="description" placeholder="Beschreibung" /><select name="produkt" aria-label="Produkt" defaultValue="olivenoel">{produkte.map((produkt) => <option key={produkt.schluessel} value={produkt.schluessel}>{produkt.name}</option>)}</select><button>Panel anlegen</button></form></AdminModal>}
    {modal?.type === 'teilnehmer' && <AdminModal title={`Teilnehmer einladen · Panel ${selectedSlug}`} onClose={() => setModal(null)}><form onSubmit={addTeilnehmer}><input required name="name" placeholder="Name" /><input required type="email" name="email" placeholder="Mailadresse" /><input name="username" placeholder="Benutzername (optional)" /><p className="configHint">Gibt es schon ein Konto mit dieser Mailadresse oder diesem Benutzernamen, wird es dem Panel zugeordnet. Danach erscheint die Einladung mit Link und QR-Code.</p><button>Einladen</button></form></AdminModal>}
    {modal?.type === 'produkt' && <AdminModal title="Neues Produkt" onClose={() => setModal(null)}><form onSubmit={createProdukt}><input required name="name" placeholder="Name, z. B. Wein" /><input name="schluessel" placeholder="Kennung (optional), z. B. wein" /><button>Produkt anlegen</button></form></AdminModal>}
    {modal?.type === 'oilSheet' && <OilSheet oil={modal.item} initialEdit={modal.edit} onClose={() => setModal(null)} onChanged={refresh} />}
    {modal?.type === 'user' && <AdminModal title={modal.item ? 'Benutzer bearbeiten' : 'Neuer Benutzer'} onClose={() => setModal(null)}><form onSubmit={modal.item ? saveUser : createUser}><input required name="name" defaultValue={modal.item?.name} placeholder="Name" /><input name="username" defaultValue={modal.item?.username || ''} placeholder="Benutzername (optional, 3–40 Zeichen)" pattern="[A-Za-z0-9._\-]{3,40}" autoCapitalize="none" /><input required type="email" name="email" defaultValue={modal.item?.email} placeholder="Mailadresse" />{!modal.item && <select name="role" defaultValue="PARTICIPANT" aria-label="Rolle"><option value="PARTICIPANT">Teilnehmer</option><option value="ORGANISATOR">Organisator</option><option value="ADMIN">Admin</option></select>}{modal.item && <><select name="role" defaultValue={modal.item.role}><option value="PARTICIPANT">Teilnehmer</option><option value="ORGANISATOR">Organisator</option><option value="ADMIN">Admin</option></select><select name="active" defaultValue={String(modal.item.active)}><option value="true">aktiv</option><option value="false">deaktiviert</option></select></>}{message && <p className="message">{message}</p>}<button>Speichern</button></form></AdminModal>}
    {modal?.type === 'oil' && <AdminModal title={modal.item ? 'Öl bearbeiten' : 'Öl anlegen'} onClose={() => setModal(null)}><form onSubmit={saveOil}><section className="imageInputs"><strong>Alternativ aus Bildern anlegen</strong>{[['FRONT_LABEL','Vorderetikett'],['BACK_LABEL','Rücketikett'],['BOTTLE','Gesamtansicht']].map(([kind,label]) => <div key={kind} className="imageSource"><label>{label}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => researchImage(kind,e.target.files?.[0])}/></label><WebcamCapture kind={kind} label={label} onCapture={researchImage}/></div>)}</section><input required name="name" value={modal.item?.name || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), name: e.target.value } })} placeholder="Name" /><input name="producer" value={modal.item?.producer || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), producer: e.target.value } })} placeholder="Hersteller" /><input name="region" value={modal.item?.region || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), region: e.target.value } })} placeholder="Herkunftsregion" /><input name="cultivar" value={modal.item?.cultivar || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), cultivar: e.target.value } })} placeholder="Sorte(n)" /><textarea name="description" value={modal.item?.description || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), description: e.target.value } })} placeholder="Beschreibung" /><textarea name="ingredientsNotes" value={modal.item?.ingredientsNotes || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), ingredientsNotes: e.target.value } })} placeholder="Inhaltsstoffe" /><textarea name="kitchenUsage" value={modal.item?.kitchenUsage || ''} onChange={e => setModal({ ...modal, item: { ...(modal.item || {}), kitchenUsage: e.target.value } })} placeholder="Kücheneinsatz" /><input name="country" value={modal.item?.country || 'Italien'} readOnly/><input name="grade" value={modal.item?.grade || 'Natives Olivenöl extra'} readOnly/><input name="category" value={modal.item?.category || 'Premiumöl'} readOnly/><div className="actions"><button type="button" className="secondary" onClick={researchOil}>Mit KI recherchieren</button>{modal.research && modal.item?.id && <button type="button" onClick={applyResearch}>Geprüften Vorschlag übernehmen</button>}<button>Speichern</button></div></form>{modal.imageResearch && <section className="researchPreview"><h3>Bildrecherche</h3><p><strong>Extrahierter Text:</strong><br />{modal.imageResearch.image.extractedText}</p><p><strong>Suchanfrage:</strong> {modal.imageResearch.searchQuery}</p><ul>{modal.imageResearch.sources.map(source => <li key={source.url}>{source.verified ? 'Geprüft' : 'Nicht geprüft'}: <a href={source.url} target="_blank" rel="noreferrer">{source.title || source.url}</a></li>)}</ul></section>}{modal.research && <section className="researchPreview"><h3>KI-Vorschlag prüfen</h3><p><strong>Hersteller:</strong> {modal.research.producerName || 'nicht ermittelt'}<br />{modal.research.producerAddress}<br />{modal.research.producerWebsite}</p><p><strong>Herkunft:</strong> {[modal.research.originRegion, modal.research.originCountry].filter(Boolean).join(', ') || 'nicht ermittelt'}</p><p><strong>Sorten:</strong> {modal.research.cultivars?.map(item => `${item.name}${item.percentage ? ` ${item.percentage} %` : ''}`).join(', ') || 'nicht ermittelt'}</p><p><strong>Quellen:</strong></p><ul>{modal.research.sources?.map(source => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title || source.url}</a></li>)}</ul></section>}</AdminModal>}
  </Shell>;
}

// Kriterienauswahl pro Panel; die Stufen (Einsteiger/Intermediate/Profi) pflegt der Admin je Produkt
// Blindprobe: Teilnehmende sehen nur Probe A, B, C … bis der Organisator die Proben aufdeckt
function BlindProbe({ panel, oils, onSaved, setMessage }) {
  async function setze(blind, aufgedeckt) {
    if (aufgedeckt && !window.confirm('Proben jetzt aufdecken? Alle Teilnehmenden sehen dann die Namen und Details.')) return;
    try { await api.updateBlind(panel.slug, { blind, aufgedeckt }); await onSaved(); } catch (error) { setMessage(error.message); }
  }
  return <section className="formCard">
    <div className="cardHeading"><h2>Blindprobe</h2></div>
    <label className="checkZeile"><input type="checkbox" checked={Boolean(panel.blind)} onChange={(event) => setze(event.target.checked, false)} /> Proben als A, B, C … verdecken</label>
    {panel.blind && <>
      <p className="configHint">{panel.aufgedeckt ? 'Die Proben sind aufgedeckt: Teilnehmende sehen Namen und Details.' : 'Teilnehmende sehen nur die Buchstaben. Die Reihenfolge entspricht der Zuordnung im Panel.'}</p>
      <ol className="blindZuordnung">{oils.map((oil, index) => <li key={oil.id}><strong>{String.fromCharCode(65 + index % 26)}</strong> {oil.name}{oil.producer ? ` · ${oil.producer}` : ''}</li>)}</ol>
      <div className="actions compactActions">
        {panel.aufgedeckt ? <button className="secondary compactButton" onClick={() => setze(true, false)}>Wieder verdecken</button> : <button className="compactButton" onClick={() => setze(true, true)}>Proben aufdecken</button>}
      </div>
    </>}
  </section>;
}

function PanelKriterien({ panel, onSaved, setMessage }) {
  const [produkt, setProdukt] = useState(null);
  const [auswahl, setAuswahl] = useState([]);
  const [geaendert, setGeaendert] = useState(false);
  useEffect(() => {
    api.produkt(panel.produkt).then((detail) => {
      setProdukt(detail);
      const aktive = detail.kriterien.filter((k) => k.aktiv).map((k) => k.schluessel);
      setAuswahl(panel.kriterien ? panel.kriterien.filter((key) => aktive.includes(key)) : aktive);
    }).catch((error) => setMessage(error.message));
  }, [panel.slug, panel.produkt]);
  if (!produkt) return null;
  const aktiveKriterien = produkt.kriterien.filter((k) => k.aktiv);
  const alle = aktiveKriterien.map((k) => k.schluessel);
  const gleich = (liste) => liste.length === auswahl.length && liste.every((key) => auswahl.includes(key));
  function setze(neu) { setAuswahl(neu); setGeaendert(true); }
  function umschalten(key) { setze(auswahl.includes(key) ? auswahl.filter((k) => k !== key) : [...auswahl, key]); }
  async function speichern() {
    try {
      // Alle aktiven Kriterien gewählt = keine Einschränkung, neue Kriterien kommen dann automatisch dazu
      await api.updatePanelKriterien(panel.slug, alle.every((key) => auswahl.includes(key)) ? null : alle.filter((key) => auswahl.includes(key)));
      setGeaendert(false);
      setMessage(`Kriterien für Panel ${panel.slug} gespeichert.`);
      await onSaved();
    } catch (error) { setMessage(error.message); }
  }
  return <section className="formCard kriterienAuswahl">
    <div className="cardHeading"><h2>Kriterien</h2><span className="kriterienAnzahl">{produkt.produkt.name} · {auswahl.length} von {alle.length} gewählt</span></div>
    <p className="configHint">Welche Kriterien sollen in diesem Panel verkostet werden? Eine Stufe übernimmt die Vorauswahl, danach lassen sich einzelne Kriterien an- und abwählen. Bereits erfasste Werte abgewählter Kriterien bleiben gespeichert, werden aber nicht mehr angezeigt.</p>
    <div className="actions compactActions">
      {Object.entries(produkt.stufen).map(([stufe, liste]) => {
        const passend = liste.filter((key) => alle.includes(key));
        return <button key={stufe} type="button" className={gleich(passend) ? 'compactButton' : 'secondary compactButton'} disabled={!passend.length} onClick={() => setze(passend)}>{stufenNamen[stufe] || stufe}</button>;
      })}
    </div>
    {aktivePhasen(aktiveKriterien).filter((phase) => phase.kriterien.length).map((phase) => <fieldset className="kriterienPhase" key={phase.key}>
      <legend>{phase.titel}</legend>
      {[...new Set(phase.kriterien.map((k) => k.gruppe))].map((gruppe) => <div className="checks" key={gruppe}>
        <strong>{gruppe}</strong>
        {phase.kriterien.filter((k) => k.gruppe === gruppe).map((k) => <label key={k.key}><input type="checkbox" checked={auswahl.includes(k.key)} onChange={() => umschalten(k.key)} />{k.label}</label>)}
      </div>)}
    </fieldset>)}
    <div className="actions"><button disabled={!geaendert || !auswahl.length} onClick={speichern}>Kriterien speichern</button></div>
  </section>;
}

// Produktseite (nur Admin): Kriterien pflegen, eigene Kriterien anlegen, Stufen festlegen
function ProduktSeite({ user, produkt }) {
  const [detail, setDetail] = useState(null);
  const [stufen, setStufen] = useState({});
  const [bearbeitet, setBearbeitet] = useState({});
  const [message, setMessage] = useState('');
  const [neu, setNeu] = useState({ phase: 'geruch', gruppe: '', label: '' });
  async function laden() {
    try { const d = await api.produkt(produkt); setDetail(d); setStufen(d.stufen); setBearbeitet({}); } catch (error) { setMessage(error.message); }
  }
  useEffect(() => { laden(); }, [produkt]);
  if (!detail) return <><Profile user={user} /><Notice title="Produkt" text={message || 'Wird geladen.'} /></>;
  const stufenListe = Object.keys(stufen);
  function kriterium(k) { return { ...k, ...(bearbeitet[k.schluessel] || {}) }; }
  function aendern(k, patch) { setBearbeitet({ ...bearbeitet, [k.schluessel]: { ...(bearbeitet[k.schluessel] || {}), ...patch } }); }
  function stufeUmschalten(stufe, key) { const liste = stufen[stufe] || []; setStufen({ ...stufen, [stufe]: liste.includes(key) ? liste.filter((x) => x !== key) : [...liste, key] }); }
  const stufenGeaendert = stufenListe.some((stufe) => JSON.stringify([...(stufen[stufe] || [])].sort()) !== JSON.stringify([...(detail.stufen[stufe] || [])].sort()));
  async function speichern() {
    try {
      for (const [key, patch] of Object.entries(bearbeitet)) {
        const k = { ...detail.kriterien.find((x) => x.schluessel === key), ...patch };
        await api.updateKriterium(produkt, key, { phase: k.phase, gruppe: k.gruppe, label: k.label, aktiv: k.aktiv });
      }
      for (const stufe of stufenListe) await api.setStufe(produkt, stufe, stufen[stufe] || []);
      setMessage('Änderungen gespeichert.');
      await laden();
    } catch (error) { setMessage(error.message); }
  }
  async function anlegen(event) {
    event.preventDefault();
    try { await api.createKriterium(produkt, neu); setNeu({ ...neu, label: '' }); setMessage(`Kriterium „${neu.label}“ angelegt.`); await laden(); } catch (error) { setMessage(error.message); }
  }
  const ungespeichert = Object.keys(bearbeitet).length > 0 || stufenGeaendert;
  return <>
    <Profile user={user} />
    <Hero compact eyebrow="Produkt" title={detail.produkt.name} text="Kriterien dieses Produkts pflegen und festlegen, welche Kriterien zu den Stufen Einsteiger, Intermediate und Profi gehören. Die Stufen dienen in den Panels als Vorauswahl." actions={<button className="secondary" onClick={() => navigate('/gustumejo/admin/')}>Zurück zur Verwaltung</button>} />
    <section className="adminArea">
      {aktivePhasen(detail.kriterien).map((phase) => {
        const liste = detail.kriterien.filter((k) => k.phase === phase.key);
        return <section className="formCard produktPhase" key={phase.key}>
          <h2>{phase.titel}</h2>
          {!liste.length && <p className="configHint">Noch keine Kriterien.</p>}
          {liste.length > 0 && <div className="kriterienTabelle" role="table">
            <div className="kriterienZeile kopf" role="row"><span role="columnheader">Kriterium</span><span role="columnheader">Gruppe</span>{stufenListe.map((stufe) => <span role="columnheader" key={stufe}>{stufenNamen[stufe] || stufe}</span>)}<span role="columnheader">aktiv</span></div>
            {liste.map((original) => { const k = kriterium(original); return <div className={k.aktiv ? 'kriterienZeile' : 'kriterienZeile inaktiv'} role="row" key={k.schluessel}>
              <input aria-label="Bezeichnung" value={k.label} onChange={(event) => aendern(original, { label: event.target.value })} />
              <input aria-label="Gruppe" value={k.gruppe} onChange={(event) => aendern(original, { gruppe: event.target.value })} />
              {stufenListe.map((stufe) => <label className="stufenHaken" key={stufe}><input type="checkbox" aria-label={`${k.label}: ${stufenNamen[stufe] || stufe}`} checked={(stufen[stufe] || []).includes(k.schluessel)} onChange={() => stufeUmschalten(stufe, k.schluessel)} /></label>)}
              <label className="stufenHaken"><input type="checkbox" aria-label={`${k.label}: aktiv`} checked={k.aktiv} onChange={(event) => aendern(original, { aktiv: event.target.checked })} /></label>
            </div>; })}
          </div>}
        </section>;
      })}
      <div className="actions"><button disabled={!ungespeichert} onClick={speichern}>Änderungen speichern</button>{ungespeichert && <span className="configHint">Es gibt ungespeicherte Änderungen.</span>}</div>
      <section className="formCard">
        <h2>Eigenes Kriterium anlegen</h2>
        <form className="adminForm neuesKriterium" onSubmit={anlegen}>
          <select value={neu.phase} onChange={(event) => setNeu({ ...neu, phase: event.target.value })} aria-label="Phase">{phasenVorlage.map((phase) => <option key={phase.key} value={phase.key}>{phase.titel}</option>)}</select>
          <input required value={neu.gruppe} onChange={(event) => setNeu({ ...neu, gruppe: event.target.value })} placeholder="Gruppe, z. B. Geruchsnote" list="kriterienGruppen" />
          <datalist id="kriterienGruppen">{[...new Set(detail.kriterien.map((k) => k.gruppe))].map((gruppe) => <option key={gruppe} value={gruppe} />)}</datalist>
          <input required value={neu.label} onChange={(event) => setNeu({ ...neu, label: event.target.value })} placeholder="Bezeichnung, z. B. Banane" />
          <button>Kriterium anlegen</button>
        </form>
        <p className="configHint">Neue Kriterien sind sofort aktiv, aber noch keiner Stufe zugeordnet. Kriterien werden nicht gelöscht, sondern deaktiviert, damit bereits erfasste Verkostungen erhalten bleiben.</p>
      </section>
      {message && <div className="adminToast" role="status"><p className="message">{message}</p><button className="secondary compactButton" aria-label="Meldung schließen" onClick={() => setMessage('')}>×</button></div>}
    </section>
  </>;
}

function AdminModal({ title, children, onClose }) { return <div className="modalBackdrop" onClick={onClose}><section className="processModal adminModal" onClick={(event) => event.stopPropagation()}><button className="closeButton" onClick={onClose}>Schließen</button><h2>{title}</h2>{children}</section></div> }
function OilSheet({ oil, initialEdit, onClose, onChanged }) {
  const [edit, setEdit] = useState(initialEdit || !oil);
  const [draft, setDraft] = useState(oil || { name: '', producer: '', region: '', country: 'Italien', cultivar: '', grade: 'Natives Olivenöl extra', category: 'Premiumöl', description: '', ingredientsNotes: '', kitchenUsage: '', sortOrder: 0 });
  const [images, setImages] = useState([]); const [imageStatus, setImageStatus] = useState(''); const [researchStatus, setResearchStatus] = useState(''); const [research, setResearch] = useState(null); const [message, setMessage] = useState('');
  const loadImages = () => { if (draft.id) api.oilImages(draft.id).then(setImages).catch(() => {}); };
  useEffect(() => { loadImages(); }, [draft.id]);
  async function save(event) { event.preventDefault(); try { const saved = oil?.id ? await api.updateOil(oil.id, draft) : await api.createOil(draft); setDraft(saved); setEdit(false); setMessage('Öl gespeichert.'); onChanged(); } catch (error) { setMessage(error.message); } }
  async function upload(kind, file) { if (!file) return; if (!draft.id) { setMessage('Bitte das Öl zuerst speichern, bevor Bilder zugeordnet werden.'); return; } setImageStatus('Bildsuche läuft'); try { const result = await imageResearch(kind, file, draft.id); setImageStatus('Bildsuche abgeschlossen'); setResearch({ labelText: result.image.extractedText, evidence: result.sources.filter(source => source.verified).map(source => ({ title: source.title, url: source.url, excerpt: source.excerpt })) }); setMessage('Bild hochgeladen und ausgewertet.'); loadImages(); } catch (error) { setImageStatus('Bildsuche fehlgeschlagen'); setMessage(error.message); } }
  async function runResearch() { setResearchStatus('Recherche läuft'); try { const result = await api.researchOil({ name: draft.name, producer: draft.producer, region: draft.region, labelText: research?.labelText || '', evidence: research?.evidence || [] }); setResearch(result); setDraft({ ...draft, producer: result.producerName || draft.producer, region: result.originRegion || draft.region, country: result.originCountry || draft.country, cultivar: result.cultivars?.map(item => item.name).join(', ') || draft.cultivar, description: result.description || draft.description, ingredientsNotes: result.ingredientsNotes || draft.ingredientsNotes, kitchenUsage: result.kitchenUsage || draft.kitchenUsage }); setResearchStatus('Recherche abgeschlossen'); } catch (error) { setResearchStatus('Recherche fehlgeschlagen'); setMessage(error.message); } }
  async function apply() { if (!draft.id || !research) return; try { const saved = await api.applyResearch(draft.id, research); setDraft(saved); setEdit(false); setMessage('Geprüfter Vorschlag übernommen.'); onChanged(); } catch (error) { setMessage(error.message); } }
  const change = (key, value) => setDraft({ ...draft, [key]: value });
  return <AdminModal title={draft.id ? 'Öl-Steckbrief' : 'Neues Öl'} onClose={onClose}>{draft.id && <button className="editButton" aria-label="Bearbeiten" onClick={() => setEdit(!edit)}>✎</button>}{message && <p className="message">{message}</p>}{!edit && <section className="oilProfile"><div className="imageGallery">{images.length ? images.map(image => <figure key={image.id}><img src={image.url} alt={image.kind} /><figcaption>{image.kind}</figcaption></figure>) : <p>Noch keine Bilder hinterlegt.</p>}</div><p className="eyebrow">{draft.category} · {draft.grade}</p><h3>{draft.name}</h3><p><strong>Hersteller:</strong> {draft.producer || 'nicht hinterlegt'}</p><p><strong>Herkunft:</strong> {[draft.region, draft.country].filter(Boolean).join(', ') || 'nicht hinterlegt'}</p><p><strong>Sorte:</strong> {draft.cultivar || 'nicht hinterlegt'}</p><p>{draft.description || 'Keine Beschreibung hinterlegt.'}</p><h3>Inhaltsstoffe</h3><p>{draft.ingredientsNotes || 'Keine Angaben hinterlegt.'}</p><h3>Kücheneinsatz</h3><p>{draft.kitchenUsage || 'Keine Angaben hinterlegt.'}</p></section>}{edit && <form className="oilEditor" onSubmit={save}><section className="imageInputs"><strong>Bilder und Bildsuche</strong>{[['FRONT_LABEL','Vorderetikett'],['BACK_LABEL','Rücketikett'],['BOTTLE','Gesamtansicht']].map(([kind,label]) => <label key={kind}>{label}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={event => upload(kind, event.target.files?.[0])}/></label>)}{imageStatus && <p className="status">{imageStatus}</p>}</section><input required value={draft.name} onChange={event => change('name', event.target.value)} placeholder="Name"/><input value={draft.producer} onChange={event => change('producer', event.target.value)} placeholder="Hersteller"/><input value={draft.region} onChange={event => change('region', event.target.value)} placeholder="Herkunftsregion"/><input value={draft.cultivar} onChange={event => change('cultivar', event.target.value)} placeholder="Sorte(n)"/><input value={draft.country || ''} onChange={event => change('country', event.target.value)} placeholder="Land"/><label className="fieldLabel">Güteklasse<select value={draft.grade || ''} onChange={event => change('grade', event.target.value)}>{[...new Set([...gradeOptions, draft.grade].filter(Boolean))].map(option => <option key={option}>{option}</option>)}</select></label><label className="fieldLabel">Kategorie<input list="oilCategories" value={draft.category || ''} onChange={event => change('category', event.target.value)} placeholder="z. B. Vergleichsöl oder Premiumöl"/></label><datalist id="oilCategories">{categoryOptions.map(option => <option key={option} value={option} />)}</datalist><textarea value={draft.description} onChange={event => change('description', event.target.value)} placeholder="Beschreibung"/><textarea value={draft.ingredientsNotes} onChange={event => change('ingredientsNotes', event.target.value)} placeholder="Inhaltsstoffe"/><textarea value={draft.kitchenUsage} onChange={event => change('kitchenUsage', event.target.value)} placeholder="Kücheneinsatz"/><div className="actions"><button type="button" className="secondary" disabled={!draft.name || researchStatus === 'Recherche läuft'} onClick={runResearch}>{researchStatus === 'Recherche läuft' ? 'Recherche läuft...' : 'Recherche'}</button><button>Speichern</button></div>{researchStatus && <p className="status">{researchStatus}</p>}{research && <section className="researchPreview"><h3>Recherchevorschlag</h3><p><strong>Hersteller:</strong> {research.producerName || 'nicht ermittelt'}<br />{research.producerAddress}<br />{research.producerWebsite}</p><p><strong>Herkunft:</strong> {[research.originRegion, research.originCountry].filter(Boolean).join(', ') || 'nicht ermittelt'}</p><p><strong>Sorten:</strong> {research.cultivars?.map(item => item.name).join(', ') || 'nicht ermittelt'}</p>{draft.id && <button type="button" onClick={apply}>Geprüften Vorschlag übernehmen</button>}</section>}</form>}</AdminModal>;
}
function Profile({ user }) {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [message, setMessage] = useState('');

  async function submitPasswordChange(event) {
    event.preventDefault();
    if (newPassword !== repeatPassword) return setMessage('Die neuen Passwörter stimmen nicht überein.');
    try {
      await api.changePassword({ currentPassword, newPassword });
      setMessage('Passwort geändert.');
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    } catch (error) {
      setMessage(error.message);
    }
  }

  return <>
    <div className={user.role !== 'PARTICIPANT' ? 'profile hasAdmin' : 'profile'}>
      <strong>{user.name}</strong>
      <span className="profileEmail">{user.email}</span>
      {user.role !== 'PARTICIPANT' && (window.location.pathname.startsWith('/gustumejo/admin')
        ? <button type="button" className="compactButton" onClick={() => navigate('/gustumejo/')}>Verkostung</button>
        : <button type="button" className="compactButton" onClick={() => navigate('/gustumejo/admin/')}>{user.role === 'ADMIN' ? 'Admin' : 'Verwaltung'}</button>)}
      <button type="button" className="secondary compactButton" onClick={() => { setShowPasswordForm(true); setMessage(''); }}><span className="labelFull">Passwort ändern</span><span className="labelShort">Passwort</span></button>
      <button className="secondary compactButton" onClick={() => api.logout().catch(() => {}).finally(() => window.location.assign('/gustumejo/'))}>Abmelden</button>
    </div>
    {showPasswordForm && <AdminModal title="Passwort ändern" onClose={() => setShowPasswordForm(false)}>
      <form onSubmit={submitPasswordChange}>
        <input required type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} placeholder="Aktuelles Passwort" />
        <input required minLength="10" type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} placeholder="Neues Passwort" />
        <input required type="password" value={repeatPassword} onChange={event => setRepeatPassword(event.target.value)} placeholder="Neues Passwort wiederholen" />
        <div className="actions compactActions">
          <button type="submit">Passwort speichern</button>
          <button type="button" className="secondary" onClick={() => setShowPasswordForm(false)}>Abbrechen</button>
        </div>
        {message && <p className="message">{message}</p>}
      </form>
    </AdminModal>}
  </>;
}

function AdminRow({ title, meta, status, children }) {
  return <div className="adminRow">
    <div className="adminRowText"><strong>{title}</strong>{meta && <span>{meta}</span>}{status && <small>{status}</small>}</div>
    <div className="adminRowActions">{children}</div>
  </div>;
}

function InvitationView({ invitation }) {
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/gustumejo/?panel=${encodeURIComponent(invitation.panel)}&invite=${invitation.token}`;
  useEffect(() => { QRCode.toDataURL(link, { width: 480, margin: 1, color: { dark: '#221f18', light: '#fffaf1' } }).then(setQr); }, [link]);
  async function copy() {
    try { await navigator.clipboard.writeText(link); setCopied(true); } catch (_) { setCopied(false); }
  }
  return <section className="invitation">
    <p className="eyebrow">{invitation.user.name}</p>
    {qr && <img src={qr} alt={`QR-Code zur Einladung für ${invitation.user.name}`} />}
    <p>{invitation.mailSent ? 'Die Einladung wurde zusätzlich per Mail verschickt. ' : ''}Zum Aktivieren den QR-Code mit dem Handy scannen oder den Link weitergeben. Der Link ist 14 Tage gültig.</p>
    <div className="actions"><button type="button" className="secondary" onClick={copy}>{copied ? 'Link kopiert' : 'Link kopieren'}</button></div>
    <a href={link} target="_blank" rel="noreferrer">{link}</a>
  </section>;
}

function Results({ session }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const panel = new URLSearchParams(window.location.search).get('panel') || session.slug;
  useEffect(() => { api.rankingSummary(panel).then(setSummary).catch((reason) => setError(reason.message)); }, [panel]);
  if (error) return <Notice title="Auswertung" text={`Die Auswertung konnte nicht geladen werden: ${error}`} />;
  if (!summary) return <Notice title="Auswertung" text="Die Eindrücke werden geladen." />;
  const count = summary.teilnehmende;
  const oils = summary.session.oils;
  const max = summary.skalaMax;
  const notizArten = [{ key: 'eindruck', label: 'Eindrücke' }, { key: 'lernnotiz', label: 'Lernnotizen' }];
  return <>
    <Hero
      compact
      eyebrow="Gemeinsame Auswertung"
      title="Sensorische Muster statt Ranking"
      text={`${count === 1 ? '1 Person hat' : `${count} Personen haben`} bisher Eindrücke erfasst. Je Kriterium siehst du, wie intensiv die Runde die Öle im Vergleich wahrgenommen hat: Mittelwert von 0 (nicht wahrnehmbar) bis ${max} (am intensivsten).`}
      actions={<button onClick={() => navigate(tastingUrl(panel))}>Zur Eingabe</button>}
    />
    {count === 0 && <section className="results"><p className="emptyResult">Noch keine Eingaben in diesem Panel.</p></section>}
    {count > 0 && aktivePhasen(summary.session.kriterienDefinitionen).map((phase) => {
      const kriterien = phase.kriterien.filter((k) => summary.kriterien[k.key]);
      if (!kriterien.length) return null;
      return <section className="auswertungPhase" key={phase.key}>
        <h2>{phase.titel}</h2>
        <div className="results">
          {kriterien.map((k) => {
            const daten = summary.kriterien[k.key];
            const anzahl = Math.max(...oils.map((oil) => daten[oil.id]?.anzahl || 0));
            return <article className="resultCard" key={k.key}>
              <p className="eyebrow">{k.gruppe} · {anzahl === 1 ? '1 Eingabe' : `${anzahl} Eingaben`}</p>
              <h3>{k.label}</h3>
              <div className="metricBars">
                {oils.map((oil, index) => <MetricBar key={oil.id} label={istVerdeckt(summary.session) ? `Probe ${probeKennung(summary.session, index)}` : `${index + 1}. ${oil.name}`} value={daten[oil.id]?.mittel || 0} max={max} />)}
              </div>
            </article>;
          })}
        </div>
      </section>;
    })}
    {count > 0 && <section className="auswertungPhase">
      <h2>Notizen</h2>
      <div className="results">
        {oils.map((oil, index) => {
          const listen = notizArten.map((art) => ({ ...art, items: summary.texte[art.key]?.[oil.id] || [] }));
          return <article className="resultCard" key={oil.id}>
            <p className="eyebrow">{[istVerdeckt(summary.session) ? 'Blindprobe' : `Öl ${index + 1}`, oil.category].filter(Boolean).join(' · ')}</p>
            <h3>{istVerdeckt(summary.session) ? `Probe ${probeKennung(summary.session, index)}` : oil.name}</h3>
            {listen.every((liste) => !liste.items.length) && <p className="emptyResult">Noch keine Notizen zu diesem Öl.</p>}
            {listen.map((liste) => <NoteList key={liste.key} title={liste.label} items={liste.items} />)}
            {!istVerdeckt(summary.session) && <button className="secondary textButton" onClick={() => navigate(tastingUrl(panel, `oel/${oil.id}`))}>Öl-Details ansehen</button>}
          </article>;
        })}
      </div>
    </section>}
  </>;
}

function MetricBar({ label, value, max = 5 }) {
  const rounded = Number(value || 0).toFixed(1);
  return <div className="metricBar">
    <span>{label}</span>
    <div className="metricTrack" role="meter" aria-label={label} aria-valuemin="0" aria-valuemax={max} aria-valuenow={rounded}><div style={{ width: `${max ? Math.min(100, ((value || 0) / max) * 100) : 0}%` }} /></div>
    <strong>{rounded.replace('.', ',')}</strong>
  </div>;
}

function NoteList({ title, items }) {
  if (!items?.length) return null;
  return <div className="noteList"><strong>{title}</strong><ul>{items.map((item, index) => <li key={index}>{item}</li>)}</ul></div>;
}

function OilDetail({ session, oilId }) {
  if (!session.oils.length) return <Shell><Notice title="Kein Öl zugeordnet" text="Für dieses Panel sind aktuell keine Ölstammdaten hinterlegt." /></Shell>;
  const index = Math.max(0, session.oils.findIndex((item) => item.id === oilId));
  if (istVerdeckt(session)) return <Shell><Notice title="Blindprobe" text="Die Proben werden erst am Ende aufgedeckt. Dann findest du hier die Details."><button onClick={() => navigate(tastingUrl(session.slug))}>Zur Verkostung</button></Notice></Shell>;
  const oil = session.oils[index];
  const previous = session.oils[index - 1];
  const next = session.oils[index + 1];
  const facts = [
    ['Güteklasse', oil.grade],
    ['Hersteller', oil.producer],
    ['Herkunft', [oil.region, oil.country].filter(Boolean).join(', ')],
    ['Sorte(n)', oil.cultivar]
  ].filter(([, value]) => value);
  const sections = [
    ['Inhaltsstoffe', 'Was fachlich relevant ist', oil.ingredientsNotes],
    ['Küche', 'Verwendung', oil.kitchenUsage]
  ].filter(([, , value]) => value);
  return <Shell>
    <Hero
      compact
      eyebrow={[`Öl ${index + 1}`, oil.category].filter(Boolean).join(' · ')}
      title={oil.name}
      text={oil.description}
      actions={<><button onClick={() => navigate(tastingUrl(session.slug, '', oil.id))}>Dieses Öl bewerten</button><button className="secondary" onClick={() => navigate(tastingUrl(session.slug, 'auswertung'))}>Auswertung</button></>}
    />
    <section className="oilFacts">
      {facts.length > 0 && <dl>{facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>}
      {sections.map(([eyebrow, title, text]) => <div className="oilFactText" key={eyebrow}><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{text}</p></div>)}
      {!oil.description && sections.length === 0 && <p className="emptyResult">Zu diesem Öl sind noch keine Beschreibung, Inhaltsstoffe oder Küchenhinweise hinterlegt.</p>}
      <nav className="oilPager" aria-label="Weitere Öle">
        {previous ? <button className="secondary" onClick={() => navigate(tastingUrl(session.slug, `oel/${previous.id}`))}>‹ {index}. {previous.name}</button> : <span />}
        {next && <button className="secondary" onClick={() => navigate(tastingUrl(session.slug, `oel/${next.id}`))}>{index + 2}. {next.name} ›</button>}
      </nav>
    </section>
  </Shell>;
}

function MarketFacts() {
  return <aside className="marketFacts">
    <h3>Marktmengen</h3>
    <p>Die Weltproduktion lag laut IOC 2023/24 bei 2,59 Mio. t und 2024/25 bei 3,57 Mio. t; für 2025/26 werden 3,44 Mio. t erwartet. Eine Aufteilung nach Güteklassen veröffentlichen weder das IOC noch die EU.</p>
    <p>Ein belegtes Beispiel: Spanische Haushalte kauften 2024 zu 49 % Olivenöl (Mischung), zu 39 % nativ extra und zu 12 % natives Olivenöl.</p>
    <ul>{marketSources.map((source) => <li key={source}><SourceLink source={source} /></li>)}</ul>
  </aside>;
}

function SourceLink({ source }) {
  const match = source.match(/^(.*?)\s*<(https?:\/\/[^>]+)>$/);
  if (!match) return source;
  return <a href={match[2]} target="_blank" rel="noreferrer">{match[1]}</a>;
}

function InfoCard({ title, text, onClick }) {
  if (onClick) return <button className="infoCard clickableCard" onClick={onClick}><h3>{title}</h3><p>{text}</p><span>Details öffnen</span></button>;
  return <div className="infoCard"><h3>{title}</h3><p>{text}</p></div>;
}
function Notice({ title, text, children }) { return <section className="notice"><h1>{title}</h1><p>{text}</p>{children && <div className="actions">{children}</div>}</section>; }
createRoot(document.getElementById('root')).render(<Rahmen><App /></Rahmen>);
